import { Injectable, inject, Injector } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { map, catchError, switchMap, filter } from 'rxjs/operators';
import { Router } from '@angular/router';
import { GenericCommanService } from '../../../../services/generic-common.service';
import { Project } from '../../../../services/project-management.service';
import { AnalysisResponse, FilterData, TabFlow } from '../../../models/generic-models';
import { GenericPromptBuilder, GenericPromptParams } from '../../../utils/generic-prompt-builder';
import { BulkSearchService } from '../../../../services/bulk-search.service';
import { BrandIndexService } from '../../../../services/brand-index.service';
import { BulkBackgroundTabsProgress, TaskStatus } from '../../../models/bulk-search.models';
import { environment } from '../../../../../environments/environment';

/** sessionStorage key: phase-2 bulk task after overview lands on project version */
export const BULK_REMAINING_TABS_SESSION_KEY = 'bulk_remaining_tabs_v1';
/** Marker for overview-first flow (phase-1 = overview only, then phase-2 in background). */
export const BULK_OVERVIEW_PROMPTS_ONLY_KEY = 'bulk_overview_prompts_only_v1';
/** Oxylabs/overview category cap; keep in sync with project-dialog `MAX_ANALYSIS_CATEGORIES` */
const MAX_ANALYSIS_CATEGORIES_OXYLABS = 5;
// Phase 2 tabs only when phase 1 runs overview-prompts-build first.
const BULK_TABS_AFTER_OVERVIEW = [
  'ai-citations',
  'consumer-insights',
  'content-creation',
  'performance-tracking',
  'ai-content-index',
  'sentiment-tracking',
  'prompt-creation',
] as const;
/**
 * Single 0–100% journey from backend (phase1 + phase2). Keep in sync with backend profile.
 * Keep in sync with `GlobalLoaderComponent.UNIFIED_BULK_STEP_COUNT` (used for unit label).
 */
export const UNIFIED_BULK_TOTAL_TABS = 14;
import { GlobalLoaderService } from '../../../global-loader/global-loader.service';
import { AnalysisStatusService } from '../../../../services/analysis-status.service';
import { ProjectHistoryService } from '../../../../services/project-history.service';
import { TaskProgressWebSocketService } from '../../../../services/task-progress-websocket.service';
import { interval } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GenericTemplateCommonService {
  /** Global loader hint during overview-first bulk (~2 min typical) */
  static readonly OVERVIEW_PHASE_ESTIMATED_SECONDS = 120;

  static overviewLoaderStageFromPercent(pct: number): { step: number; total: number; label: string } {
    const p = Math.min(100, Math.max(0, pct));
    if (p < 34) {
      return { step: 1, total: 3, label: 'Fetching overview data' };
    }
    if (p < 67) {
      return { step: 2, total: 3, label: 'Processing insights' };
    }
    return { step: 3, total: 3, label: 'Finalizing overview' };
  }

  private isCacheStatus(status: TaskStatus): boolean {
    const text = [
      status.current_step || '',
      status.current_tab || '',
      ...(status.status_messages || []),
    ]
      .join(' ')
      .toLowerCase();
    return (
      text.includes('cache hit') ||
      text.includes('served from cache') ||
      text.includes('reusing') ||
      text.includes('reuse')
    );
  }

  /** Remove provider/service names from user-facing loader labels. */
  private sanitizeLoaderLabel(raw: string | undefined | null): string {
    let s = (raw || 'Working...').trim();
    s = s
      .replace(/\s*\(?\s*Oxylabs\s*\)?/gi, '')
      .replace(/\s*\(?\s*Tavily\s*\)?/gi, '')
      .replace(/\s*\(?\s*ScraperAPI\s*\)?/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
    return s || 'Working...';
  }

  // BehaviorSubjects for reactive data flow
  private filterDataSubject = new BehaviorSubject<FilterData | null>(null);
  private projectDataSubject = new BehaviorSubject<Project | null>(null);
  private analysisResponseSubject = new BehaviorSubject<AnalysisResponse | null | any>(null);
  private aiCitationsResponseSubject = new BehaviorSubject<any | null>(null);
  private categoryAnalysisResponseSubject = new BehaviorSubject<any | null>(null);
  private sentimentTrackingResponseSubject = new BehaviorSubject<any | null>(null);
  private aiContentIndexResponseSubject = new BehaviorSubject<any | null>(null);
  private performanceTrackingResponseSubject = new BehaviorSubject<any | null>(null);
  private consumerInsightsResponseSubject = new BehaviorSubject<any | null>(null);
  private contentCreationResponseSubject = new BehaviorSubject<any | null>(null);
  private eeatAuditResponseSubject = new BehaviorSubject<any | null>(null);
  private landingPageAuditResponseSubject = new BehaviorSubject<any | null>(null);
  private aiAuditResponseSubject = new BehaviorSubject<any | null>(null);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);
  private tabFlowSubject = new BehaviorSubject<TabFlow>({ type: 'education', activeTab: 'overview' });
  private compareClickedSubject = new BehaviorSubject<boolean>(false);
  private analysisCompletedSubject = new BehaviorSubject<boolean>(false);
  
  // Industry caching
  private industryCacheSubject = new BehaviorSubject<Map<string, string>>(new Map());
  private isIndustryDetectionInProgress = false;
  
  // Flag to prevent multiple simultaneous API calls
  private isApiCallInProgress = false;
  
  // Track which tab is currently making an API call to prevent duplicates
  private tabApiCallInProgress = new Map<string, boolean>();
  private tabReloadRetryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  
  // Flag to prevent recursive data processing
  private isProcessingData = false;
  
  // Flag to track if bulk search is in progress
  private isBulkSearchWithOverviewInProgress = false;
  /** When true, completion of current bulk task is category-analysis-only reload (no page redirect). */
  private isCategoryAnalysisReloadOnly = false;
  /** When true, completion of current bulk task is overview-only reload (no page redirect). */
  private isOverviewReloadOnly = false;
  
  // Bulk search tracking
  private bulkSearchProgressSubject = new BehaviorSubject<TaskStatus | null>(null);
  private currentTaskIdSubject = new BehaviorSubject<string | null>(null);
  
  // LLM comparison metrics tracking
  private llmComparisonMetricsProgressSubject = new BehaviorSubject<TaskStatus | null>(null);
  private llmComparisonMetricsTaskIdSubject = new BehaviorSubject<string | null>(null);
  private llmComparisonMetricsPollingInterval?: any;
  private llmComparisonMetricsWsSubscription?: Subscription;
  /** Emits full versionData when LLM comparison task completes - page should distribute to all tabs */
  private llmComparisonCompleteWithDataSubject = new Subject<any>();
  private backgroundTabsProgressSubject = new BehaviorSubject<BulkBackgroundTabsProgress | null>(null);
  private lastBulkFiltersSnapshot: Record<string, unknown> | null = null;
  private resumeBulkLoaderDismissedForTask: string | null = null;
  /** Overview → AI Citations "Sources by domain", etc. */
  private requestOpenTabWithScrollSubject = new Subject<{ tabId: string; scrollToId?: string }>();

  // Observables for components to subscribe to
  public filterData$ = this.filterDataSubject.asObservable();
  public projectData$ = this.projectDataSubject.asObservable();
  public analysisResponse$ = this.analysisResponseSubject.asObservable();
  public aiCitationsResponse$ = this.aiCitationsResponseSubject.asObservable();
  public categoryAnalysisResponse$ = this.categoryAnalysisResponseSubject.asObservable();
  public sentimentTrackingResponse$ = this.sentimentTrackingResponseSubject.asObservable();
  public aiContentIndexResponse$ = this.aiContentIndexResponseSubject.asObservable();
  public performanceTrackingResponse$ = this.performanceTrackingResponseSubject.asObservable();
  public consumerInsightsResponse$ = this.consumerInsightsResponseSubject.asObservable();
  public contentCreationResponse$ = this.contentCreationResponseSubject.asObservable();
  public eeatAuditResponse$ = this.eeatAuditResponseSubject.asObservable();
  public landingPageAuditResponse$ = this.landingPageAuditResponseSubject.asObservable();
  public aiAuditResponse$ = this.aiAuditResponseSubject.asObservable();
  public isLoading$ = this.isLoadingSubject.asObservable();
  public error$ = this.errorSubject.asObservable();
  public tabFlow$ = this.tabFlowSubject.asObservable();
  public compareClicked$ = this.compareClickedSubject.asObservable();
  public analysisCompleted$ = this.analysisCompletedSubject.asObservable();
  public industryCache$ = this.industryCacheSubject.asObservable();
  public bulkSearchProgress$ = this.bulkSearchProgressSubject.asObservable();
  public currentTaskId$ = this.currentTaskIdSubject.asObservable();
  public llmComparisonMetricsProgress$ = this.llmComparisonMetricsProgressSubject.asObservable();
  public llmComparisonMetricsTaskId$ = this.llmComparisonMetricsTaskIdSubject.asObservable();
  /** Emits versionData when LLM comparison task completes - subscribe to distribute to tabs */
  public llmComparisonCompleteWithData$ = this.llmComparisonCompleteWithDataSubject.asObservable();
  public backgroundTabsProgress$ = this.backgroundTabsProgressSubject.asObservable();
  /** Category-analysis backfill / reload: queued → running (with optional bulk task_id). */
  private categoryAnalysisBackgroundLoadSubject = new BehaviorSubject<{
    phase: 'idle' | 'queued' | 'running';
    taskId: string | null;
  }>({ phase: 'idle', taskId: null });
  public categoryAnalysisBackgroundLoad$ = this.categoryAnalysisBackgroundLoadSubject.asObservable();
  public requestOpenTabWithScroll$ = this.requestOpenTabWithScrollSubject.asObservable();

  /** Switch to a tab (e.g. ai-citations) and optionally scroll to element id after render. */
  requestNavigateToTab(tabId: string, scrollToId?: string): void {
    this.requestOpenTabWithScrollSubject.next({ tabId, scrollToId });
  }

  getCurrentTaskId(): string | null {
    return this.currentTaskIdSubject.value;
  }

  getCurrentLLMComparisonMetricsTaskId(): string | null {
    return this.llmComparisonMetricsTaskIdSubject.value;
  }

  /**
   * Poll phase-2 bulk (remaining tabs) after redirect; shows silent Overview header progress.
   * Progress uses the same 0–100 scale as the main loader: phase 1 = first 8/14, phase 2 = last 6/14.
   */
  startSilentRemainingTabsPolling(
    taskId: string,
    onTerminal?: (status: TaskStatus) => void
  ): void {
    this.resumeBulkLoaderDismissedForTask = null;
    this.currentTaskIdSubject.next(taskId);
    this.globalLoaderService.resetLoader();
    // Show header animation immediately, even before first status tick/websocket message.
    this.backgroundTabsProgressSubject.next({
      loaded: Math.round((57.14 / 100) * UNIFIED_BULK_TOTAL_TABS),
      total: UNIFIED_BULK_TOTAL_TABS,
      currentTab: 'Starting other tabs...',
      taskId,
    });
    this.bulkSearchService.startProgressPolling(taskId, (status: TaskStatus) => {
      const unifiedPct = Math.min(100, Math.max(0, status.progress_percentage ?? 0));
      const loaded = Math.min(
        UNIFIED_BULK_TOTAL_TABS,
        Math.round((unifiedPct / 100) * UNIFIED_BULK_TOTAL_TABS)
      );
      const label = (status.current_tab || '').replace(/-/g, ' ') || '…';
      this.backgroundTabsProgressSubject.next({
        loaded,
        total: UNIFIED_BULK_TOTAL_TABS,
        currentTab: label,
        taskId,
      });
      if (
        status.status === 'completed' ||
        status.status === 'failed' ||
        status.status === 'cancelled'
      ) {
        this.backgroundTabsProgressSubject.next(null);
        this.bulkSearchService.stopProgressPolling();
        this.currentTaskIdSubject.next(null);
        if (status.status === 'completed') {
          const pdid = status.projectdata_id || this.currentProjectdataId || this.getProjectContextFromRouter()?.projectdataId;
          if (pdid) {
            this.projectHistoryService.getVersionData(pdid).subscribe({
              next: (vd) => this.applyVersionDataAfterBackgroundBulk(vd),
              error: (e) => console.error('Failed to refresh version data after background bulk completion', e),
            });
          }
        }
        onTerminal?.(status);
      }
    });
  }

  private applyVersionDataAfterBackgroundBulk(versionData: any): void {
    const allTabsData = versionData?.data;
    if (!allTabsData || typeof allTabsData !== 'object') return;

    const opb = allTabsData['overview-prompts-build'];
    if (opb && !allTabsData.overview) {
      this.updateAnalysisResponse(opb);
    } else if (allTabsData.overview) {
      this.updateAnalysisResponse(allTabsData.overview);
    }
    if (allTabsData['ai-citations']) this.updateAiCitationsResponse(allTabsData['ai-citations']);
    if (allTabsData['category-analysis']) this.updateCategoryAnalysisResponse(allTabsData['category-analysis']);
    if (allTabsData['consumer-insights']) this.updateConsumerInsightsResponse(allTabsData['consumer-insights']);
    if (allTabsData['content-creation']) this.updateContentCreationResponse(allTabsData['content-creation']);
    if (allTabsData['performance-tracking']) this.updatePerformanceTrackingResponse(allTabsData['performance-tracking']);
    if (allTabsData['ai-content-index']) this.updateAiContentIndexResponse(allTabsData['ai-content-index']);
    if (allTabsData['sentiment-tracking']) this.updateSentimentTrackingResponse(allTabsData['sentiment-tracking']);
  }

  private hasRealCategoryAnalysisData(response: any): boolean {
    const sd = response?.structured_data || response;
    if (!sd || typeof sd !== 'object') return false;
    return Boolean(
      (Array.isArray(sd.brands) && sd.brands.length > 0) ||
      (sd.growing_prompts && Object.keys(sd.growing_prompts || {}).length > 0) ||
      (Array.isArray(sd.category_trends) && sd.category_trends.length > 0) ||
      (Array.isArray(sd.most_cited_domains) && sd.most_cited_domains.length > 0)
    );
  }

  cancelBackgroundBulkTabs(): void {
    const tid = this.currentTaskIdSubject.value;
    if (!tid) {
      this.backgroundTabsProgressSubject.next(null);
      return;
    }
    this.bulkSearchService.cancelTask(tid).subscribe({
      next: () => {
        this.backgroundTabsProgressSubject.next(null);
        this.bulkSearchService.stopProgressPolling();
        this.currentTaskIdSubject.next(null);
      },
      error: (e) => console.error('Cancel background bulk failed', e),
    });
  }

  /**
   * On page reload, restore phase-2/other-tabs background progress using active analysis status + bulk task status.
   * This covers sessions where BULK_REMAINING_TABS_SESSION_KEY is missing but backend task is still running.
   */
  resumeBackgroundTabsFromActiveStatus(
    projectId?: string,
    projectdataId?: string,
    onTerminal?: (status: TaskStatus) => void
  ): void {
    this.analysisStatusService.checkActiveAnalysis().subscribe({
      next: (status) => {
        const active = !!status?.has_active_analysis && !!status?.task_id && status?.status !== 'cancelled';
        if (!active) return;
        if (projectId && status.project_id && status.project_id !== projectId) return;
        if (this.getCurrentTaskId() === status.task_id) return;

        this.bulkSearchService
          .getTaskStatus(status.task_id!)
          .then((taskStatus) => {
            const taskActive =
              taskStatus.status === 'pending' ||
              taskStatus.status === 'queued' ||
              taskStatus.status === 'progress' ||
              taskStatus.status === 'in_progress';
            if (!taskActive) return;
            if (projectdataId && taskStatus.projectdata_id && taskStatus.projectdata_id !== projectdataId) return;
            // Ignore LLM-visibility/background metric tasks and only resume multi-tab bulk tasks.
            if ((taskStatus.total_tabs ?? 0) <= 1) return;
            this.startSilentRemainingTabsPolling(status.task_id!, onTerminal);
          })
          .catch(() => {
            // Not a bulk task status or no longer available.
          });
      },
      error: () => {
        // Best-effort resume only.
      },
    });
  }

  private injector = inject(Injector);

  constructor(
    private genericService: GenericCommanService,
    private bulkSearchService: BulkSearchService,
    private brandIndexService: BrandIndexService,
    private globalLoaderService: GlobalLoaderService,
    private analysisStatusService: AnalysisStatusService,
    private projectHistoryService: ProjectHistoryService,
    private taskProgressWs: TaskProgressWebSocketService,
    private router: Router,
  ) {}

  // Methods to update data
  updateFilterData(filterData: FilterData, forceReload: boolean = true): void {
    this.filterDataSubject.next(filterData);
  }

  /**
   * Get industry from database (ProjectData filters) if projectdataId exists
   */
  private getIndustryFromDatabase(projectdataId: string): Observable<string | null> {
    if (!projectdataId) {
      return of(null);
    }
    
    console.log(`📦 Attempting to get industry from database (projectdataId: ${projectdataId})`);
    
    return this.projectHistoryService.getVersionData(projectdataId).pipe(
      map((versionData) => {
        if (versionData?.filters?.industry) {
          const industry = versionData.filters.industry;
          console.log(`✅ Found industry in database: ${industry}`);
          
          // Cache it for future use
          if (versionData.filters.brands && versionData.filters.brands.length > 0) {
            const primaryBrand = versionData.filters.brands[0];
            const cleanedBrandName = primaryBrand.replace(/\s*\(YOU\)\s*/g, '').trim();
            const currentCache = this.industryCacheSubject.value;
            currentCache.set(cleanedBrandName, industry);
            this.industryCacheSubject.next(new Map(currentCache));
            console.log(`✅ Cached industry from DB for ${cleanedBrandName}: ${industry}`);
          }
          
          return industry;
        }
        console.log(`⚠️ No industry found in database filters`);
        return null;
      }),
      catchError((error: any) => {
        console.warn(`⚠️ Failed to get industry from database:`, error);
        return of(null);
      })
    );
  }

  /**
   * Get industry from project (DB only). No API calls - industry/category set once at project creation.
   */
  private getIndustryFromProject(_primaryBrand: string): string | null {
    const project = this.projectDataSubject.value;
    return project?.industry ?? null;
  }

  /**
   * Categories for overview-prompts-build (Oxylabs): analysis_categories from project save;
   * legacy projects without it → first N of project.categories (same cap as project dialog).
   */
  private categoriesForOverviewOxylabs(projectData: Project | null): string[] | undefined {
    if (!projectData) return undefined;
    const ac = projectData.analysis_categories;
    if (Array.isArray(ac) && ac.length > 0) {
      const names = ac.map((s) => String(s).trim()).filter(Boolean);
      if (names.length) return names;
    }
    if (projectData.categories?.length) {
      return projectData.categories
        .slice(0, MAX_ANALYSIS_CATEGORIES_OXYLABS)
        .map((c) => c.name);
    }
    return undefined;
  }

  /**
   * Try to get industry from project or cache only (no API). Analysis/re-analysis read from DB only.
   */
  private tryGetIndustryFromCacheOrAPI(
    primaryBrand: string,
    filterData: FilterData,
    useBulkSearch: boolean,
    projectId?: string,
    projectdataId?: string
  ): void {
    const cleanedBrandName = primaryBrand.replace(/\s*\(YOU\)\s*/g, '').trim();
    const fromProject = this.getIndustryFromProject(primaryBrand);
    if (fromProject) {
      const currentCache = this.industryCacheSubject.value;
      currentCache.set(cleanedBrandName, fromProject);
      this.industryCacheSubject.next(new Map(currentCache));
      console.log(`✅ Using industry from project for ${primaryBrand}: ${fromProject}`);
      this.loadFunction(filterData, useBulkSearch, filterData.hardReload, projectId, projectdataId);
      return;
    }
    const cachedIndustry = this.getCachedIndustry(primaryBrand);
    if (cachedIndustry) {
      console.log(`✅ Using cached industry for ${primaryBrand}: ${cachedIndustry}`);
      this.loadFunction(filterData, useBulkSearch, filterData.hardReload, projectId, projectdataId);
    } else {
      const currentCache = this.industryCacheSubject.value;
      currentCache.set(cleanedBrandName, 'General');
      this.industryCacheSubject.next(new Map(currentCache));
      console.log(`⚠️ No project/cached industry for ${primaryBrand}, using 'General'`);
      this.loadFunction(filterData, useBulkSearch, filterData.hardReload, projectId, projectdataId);
    }
  }

  updateProjectData(project: Project): void {
    this.projectDataSubject.next(project);
    // Seed industry cache from project (industry/category set once at creation; analysis never calls API)
    if (project?.industry && project?.name) {
      const cleanedBrandName = project.name.replace(/\s*\(YOU\)\s*/g, '').trim();
      const currentCache = this.industryCacheSubject.value;
      currentCache.set(cleanedBrandName, project.industry);
      this.industryCacheSubject.next(new Map(currentCache));
    }
    // Update filter data to include project creation date
    const currentFilterData = this.filterDataSubject.value;
    if (currentFilterData) {
      const updatedFilterData: FilterData = {
        ...currentFilterData,
        projectCreatedAt: project.created_at
      };
      this.filterDataSubject.next(updatedFilterData);
    }
  }

  handleFilterChange(filterData: FilterData): void {
    // Merge incoming (often partial from filter UI) with current – preserve category/categories/industry/base_domain/description from project/history
    const currentFilterData = this.filterDataSubject.value;
    const currentProject = this.projectDataSubject.value;
    const projectCreatedAt = filterData.projectCreatedAt || currentFilterData?.projectCreatedAt || currentProject?.created_at;

    const fullFilterData: FilterData = {
      selectedEntities: filterData.selectedEntities ?? currentFilterData?.selectedEntities ?? [],
      category: filterData.category ?? currentFilterData?.category,
      categories: filterData.categories ?? currentFilterData?.categories,
      industry: filterData.industry ?? currentFilterData?.industry,
      country: filterData.country ?? currentFilterData?.country ?? 'Global',
      models: filterData.models ?? currentFilterData?.models ?? ['gpt-4', 'claude-3'],
      experience_level: filterData.experience_level ?? currentFilterData?.experience_level ?? 'intermediate',
      budget_range: filterData.budget_range ?? currentFilterData?.budget_range ?? 'premium',
      projectCreatedAt,
      base_domain: filterData.base_domain ?? currentFilterData?.base_domain,
      description: filterData.description ?? currentFilterData?.description,
      hardReload: filterData.hardReload ?? currentFilterData?.hardReload,
    };
    this.filterDataSubject.next(fullFilterData);
  }

  initializeFilterData(selectedEntities: any[], category: string, country: string, additionalData?: Partial<FilterData>): void {
    // Get project creation date from current project data
    const currentProject = this.projectDataSubject.value;
    const projectCreatedAt = currentProject?.created_at || additionalData?.projectCreatedAt;
    
    const filterData: FilterData = {
      selectedEntities,
      category,
      country,
      models: additionalData?.models || ['gpt-4', 'claude-3'],
      experience_level: additionalData?.experience_level || 'intermediate',
      budget_range: additionalData?.budget_range || 'premium',
      projectCreatedAt: projectCreatedAt
    };
    this.filterDataSubject.next(filterData);
  }

  updateAnalysisResponse(response: AnalysisResponse): void {
    this.analysisResponseSubject.next(response);
  }

  updateAiCitationsResponse(response: any): void {
    this.aiCitationsResponseSubject.next(response);
  }

  updateCategoryAnalysisResponse(response: any): void {
    this.categoryAnalysisResponseSubject.next(response);
  }

  updateSentimentTrackingResponse(response: any): void {
    this.sentimentTrackingResponseSubject.next(response);
  }

  updateAiContentIndexResponse(response: any): void {
    this.aiContentIndexResponseSubject.next(response);
  }

  updatePerformanceTrackingResponse(response: any): void {
    this.performanceTrackingResponseSubject.next(response);
  }

  updateConsumerInsightsResponse(response: any): void {
    this.consumerInsightsResponseSubject.next(response);
  }
  
  updateContentCreationResponse(response: any): void {
    this.contentCreationResponseSubject.next(response);
  }

  updateEeatAuditResponse(response: any): void {
    this.eeatAuditResponseSubject.next(response);
  }

  updateLandingPageAuditResponse(response: any): void {
    this.landingPageAuditResponseSubject.next(response);
  }

  updateAIAuditResponse(response: any): void {
    this.aiAuditResponseSubject.next(response);
  }

  setLoading(loading: boolean): void {
    this.isLoadingSubject.next(loading);
  }

  setError(error: string | null): void {
    this.errorSubject.next(error);
  }

  /**
   * Safely process data to prevent recursive calls and console spam
   */
  private processDataSafely(data: any, tabType: string): void {
    if (this.isProcessingData) {
      console.warn('⚠️ Data processing already in progress, skipping...');
      return;
    }
    
    this.isProcessingData = true;
    try {
      console.log(`🔍 Processing ${tabType} data safely:`, data);
      // Data processing logic will be handled by individual components
    } catch (error) {
      console.error(`❌ Data processing error for ${tabType}:`, error);
      this.setError(`Data processing failed for ${tabType}`);
    } finally {
      this.isProcessingData = false;
    }
  }

  updateTabFlow(tabFlow: TabFlow): void {
    this.tabFlowSubject.next(tabFlow);
  }

  setCompareClicked(clicked: boolean): void {
    this.compareClickedSubject.next(clicked);
    // Ensure global progress loader shows immediately when analysis is initiated
    if (clicked) {
      this.globalLoaderService.initializeProgress();
      this.globalLoaderService.updateProgressPercentage(0);
    } else {
      this.globalLoaderService.resetLoader();
    }
  }

  getCompareClicked(): boolean {
    return this.compareClickedSubject.value;
  }

  triggerAnalysis(filterData: FilterData, useBulkSearch: boolean = false, projectId?: string, projectdataId?: string): void {
    if (this.isApiCallInProgress) {
      return;
    }

    if (filterData.selectedEntities.length === 0) {
      this.setError('Please select at least one entity to analyze');
      return;
    }

    const currentTabFlow = this.getCurrentTabFlow();

    if (!filterData.country) {
      this.setError('Please select a country');
      return;
    }

    // If using bulk search, initialize global loader immediately
    // This ensures the progress loader shows right away, regardless of which tab user is on
    if (useBulkSearch) {
      console.log('🚀 Initializing global loader for bulk analysis');
      this.globalLoaderService.initializeProgress();
      this.globalLoaderService.updateProgressPercentage(0);
      // Don't use normal loading state for bulk search - use global loader instead
      this.setLoading(false);
    } else {
      this.setLoading(true);
    }
    
    this.setError(null);
    this.isApiCallInProgress = true;

    if (currentTabFlow.type === 'brand-analysis' || currentTabFlow.type === 'generic') {
      const primaryBrand = filterData?.selectedEntities?.[0]?.name;
      if (primaryBrand) {
        // Ensure domain is in filterData - extract from project if not present
        if (!filterData.base_domain && !filterData.url && !filterData.domain) {
          const projectData = this.getCurrentProjectData();
          const projectDescription = projectData?.description || '';
          if (projectDescription) {
            const baseDomain = this.extractBaseDomain(projectDescription);
            if (baseDomain) {
              // Update filterData with domain
              const updatedFilterData: FilterData = {
                ...filterData,
                base_domain: baseDomain,
                description: projectDescription
              };
              this.updateFilterData(updatedFilterData);
              filterData = updatedFilterData; // Use updated filterData
            }
          }
        }
        
        // Priority: 1. Database (if projectdataId exists), 2. Cache, 3. API
        if (projectdataId) {
          // Try to get industry from database first
          this.getIndustryFromDatabase(projectdataId).subscribe({
            next: (dbIndustry: string | null) => {
              if (dbIndustry) {
                console.log(`✅ Using industry from database: ${dbIndustry}`);
                this.loadFunction(filterData, useBulkSearch, filterData.hardReload, projectId, projectdataId);
              } else {
                // Fall back to cache or API
                this.tryGetIndustryFromCacheOrAPI(primaryBrand, filterData, useBulkSearch, projectId, projectdataId);
              }
            },
            error: (error: any) => {
              console.warn(`⚠️ Failed to get industry from database, falling back to cache/API`);
              this.tryGetIndustryFromCacheOrAPI(primaryBrand, filterData, useBulkSearch, projectId, projectdataId);
            }
          });
        } else {
          // No projectdataId, try cache or API
          this.tryGetIndustryFromCacheOrAPI(primaryBrand, filterData, useBulkSearch, projectId, projectdataId);
        }
      } else {
        this.loadFunction(filterData, useBulkSearch, filterData.hardReload, projectId, projectdataId);
      }
    } else {
      this.analysisResponseSubject.next(true);
      this.isApiCallInProgress = false;
    }
  }

  // Store project info for bulk search and LLM comparison metrics
  private currentProjectId: string | null = null;
  private currentProjectdataId: string | null = null;

  /**
   * Set project context (projectId, projectdataId) for API calls that need to persist to projectdata.
   * Call this when loading a project/version so that LLM AI visibility and other endpoints receive
   * project_id and projectdata_id in the payload (required for saving results to DB).
   */
  setProjectContext(projectId?: string | null, projectdataId?: string | null): void {
    this.currentProjectId = projectId ?? null;
    this.currentProjectdataId = projectdataId ?? null;
    if (projectId || projectdataId) {
      console.log(`📦 Project context set: projectId=${projectId || 'none'}, projectdataId=${projectdataId || 'none'}`);
    }
  }

  /** Get current project context for API payloads (used by tabs that trigger LLM comparison reload). */
  getCurrentProjectContext(): { projectId: string | null; projectdataId: string | null } {
    return { projectId: this.currentProjectId, projectdataId: this.currentProjectdataId };
  }

  loadFunction(filterData: FilterData, useBulkSearch: boolean = false, forceRefresh: boolean = false, projectId?: string, projectdataId?: string) {
    // Store project info for use in bulk search
    this.currentProjectId = projectId || null;
    this.currentProjectdataId = projectdataId || null;
    
    if (useBulkSearch) {
      this.startBulkAnalysisWithOverview(filterData, forceRefresh);
    } else {
      this.callGenericOverviewAPI(filterData, forceRefresh);
    }
  }
  triggerReload(forceRefresh: boolean = true): void {
    if (this.isApiCallInProgress) {
      return;
    }

    const currentFilterData = this.getCurrentFilterData();
    if (!currentFilterData) {
      this.setError('No filter data available for reload');
      return;
    }

    this.setLoading(true);
    this.setError(null);
    this.isApiCallInProgress = true;

    this.callGenericOverviewAPIWithReload(currentFilterData, forceRefresh);
  }

  triggerTabAnalysis(tabType: string, forceCache: boolean = false, hardReload: boolean = false): void {
    // Backend-first tab load: avoid in-memory reuse on tab switches.
    console.log(`🔄 Triggering ${tabType} tab - forcing backend reload`);
    this.triggerTabReload(tabType, true);
  }

  /**
   * Overview-first bulk returns minimal category-analysis. Rebuild from AI Visibility + discovery
   * competitor discovery prompts (no Gemini category bulk — avoids restarting overview-scale jobs).
   */
  scheduleCategoryAnalysisBackfillAfterMinimalBulk(): void {
    let attempts = 0;
    const maxAttempts = 80;
    const tick = () => {
      attempts++;
      if (attempts > maxAttempts) {
        if (this.categoryAnalysisBackgroundLoadSubject.value.phase !== 'idle') {
          this.categoryAnalysisBackgroundLoadSubject.next({ phase: 'idle', taskId: null });
        }
        return;
      }
      if (this.isApiCallInProgress) {
        setTimeout(tick, 500);
        return;
      }
      const pid = this.currentProjectdataId || this.getProjectContextFromRouter()?.projectdataId;
      if (!pid) {
        setTimeout(tick, 400);
        return;
      }
      this.categoryAnalysisBackgroundLoadSubject.next({ phase: 'running', taskId: `rebuild-${pid.slice(0, 8)}` });
      this.isApiCallInProgress = true;
      this.genericService.rebuildCategoryAnalysisFromVisibility(pid).subscribe({
        next: () => {
          this.isApiCallInProgress = false;
          this.categoryAnalysisBackgroundLoadSubject.next({ phase: 'idle', taskId: null });
          this.projectHistoryService.getVersionData(pid).subscribe({
            next: (vd) => {
              const tab = vd?.data?.['category-analysis'];
              if (tab) {
                this.updateCategoryAnalysisResponse(tab);
              }
            }
          });
        },
        error: (err) => {
          console.warn('rebuild-category-analysis failed', err);
          this.isApiCallInProgress = false;
          this.categoryAnalysisBackgroundLoadSubject.next({ phase: 'idle', taskId: null });
        }
      });
    };
    setTimeout(tick, 800);
  }

  /** User-initiated: rebuild category tab from DB discovery + saved AI Visibility (no Gemini category bulk). */
  rebuildCategoryAnalysisFromSavedVisibility(): void {
    const pid = this.currentProjectdataId || this.getProjectContextFromRouter()?.projectdataId;
    if (!pid) {
      this.triggerTabReload('category-analysis', true);
      return;
    }
    this.setLoading(true);
    this.categoryAnalysisBackgroundLoadSubject.next({ phase: 'running', taskId: `rebuild-${pid.slice(0, 8)}` });
    this.genericService.rebuildCategoryAnalysisFromVisibility(pid).subscribe({
      next: () => {
        this.setLoading(false);
        this.categoryAnalysisBackgroundLoadSubject.next({ phase: 'idle', taskId: null });
        this.projectHistoryService.getVersionData(pid).subscribe({
          next: (vd) => {
            const tab = vd?.data?.['category-analysis'];
            if (tab) {
              this.updateCategoryAnalysisResponse(tab);
            }
          }
        });
      },
      error: () => {
        this.setLoading(false);
        this.categoryAnalysisBackgroundLoadSubject.next({ phase: 'idle', taskId: null });
        this.triggerTabReload('category-analysis', true);
      }
    });
  }

  triggerTabReload(tabType: string, forceRefresh: boolean = true): void {
    if (this.isApiCallInProgress) {
      // Avoid dropping tab reload requests while another tab call is in-flight.
      if (!this.tabReloadRetryTimers.has(tabType)) {
        const timer = setTimeout(() => {
          this.tabReloadRetryTimers.delete(tabType);
          this.triggerTabReload(tabType, forceRefresh);
        }, 500);
        this.tabReloadRetryTimers.set(tabType, timer);
      }
      if (tabType === 'category-analysis' && forceRefresh) {
        this.categoryAnalysisBackgroundLoadSubject.next({ phase: 'idle', taskId: null });
      }
      return;
    }

    let currentFilterData = this.getCurrentFilterData();
    
    if (!currentFilterData && tabType !== 'ai-content-index') {
      if (tabType === 'category-analysis') {
        this.categoryAnalysisBackgroundLoadSubject.next({ phase: 'idle', taskId: null });
      }
      this.setError('No filter data available for tab reload');
      return;
    }

    // Ensure projectCreatedAt is set - get from project data if not in filterData
    if (currentFilterData && !currentFilterData.projectCreatedAt) {
      const currentProject = this.projectDataSubject.value;
      const projectCreatedAt = currentProject?.created_at;
      
      if (projectCreatedAt) {
        currentFilterData = {
          ...currentFilterData,
          projectCreatedAt: projectCreatedAt
        };
        this.filterDataSubject.next(currentFilterData);
        console.log(`🔧 Tab reload: Using project creation date: ${projectCreatedAt}`);
      } else {
        // Fallback to current date only if no project data available
        currentFilterData = {
          ...currentFilterData,
          projectCreatedAt: new Date().toISOString()
        };
        this.filterDataSubject.next(currentFilterData);
        console.warn(`🔧 Tab reload: No project data found, using current date for cache key`);
      }
    }

    this.setLoading(true);
    this.setError(null);
    this.isApiCallInProgress = true;

    // Category-analysis reload: run only that tab on backend and update tab without redirect
    if (tabType === 'category-analysis' && forceRefresh) {
      this.startCategoryAnalysisReload(currentFilterData!);
      return;
    }

    // Overview reload: run only overview tab on backend and update tab in place (no full project reload)
    if (tabType === 'overview' && forceRefresh) {
      this.startOverviewReload(currentFilterData!);
      return;
    }

    // AI Citations reload: run only ai-citations tab on backend and update tab in place (same as Category Analysis)
    if (tabType === 'ai-citations' && forceRefresh) {
      this.startAiCitationsReload(currentFilterData!);
      return;
    }

    // AI Content Index reload: run only ai-content-index tab on backend and update tab in place (same as Overview / Category Analysis)
    if (tabType === 'ai-content-index' && forceRefresh) {
      this.startAiContentIndexReload(currentFilterData!);
      return;
    }

    // Other tab reloads: run a single tab via bulk-search and update that tab from latest version data.
    const isStandardReloadTab = (
      t: string
    ): t is 'sentiment-tracking' | 'consumer-insights' | 'content-creation' | 'performance-tracking' => {
      return ['sentiment-tracking', 'consumer-insights', 'content-creation', 'performance-tracking'].includes(t);
    };
    if (forceRefresh && isStandardReloadTab(tabType)) {
      this.startStandardSingleTabReload(currentFilterData!, tabType);
      return;
    }

    // AI Visibility reload: run only ai-visibility tab (RAG-based scoring), merges into overview and updates projectdata
    if (tabType === 'ai-visibility' && forceRefresh) {
      this.startAiVisibilityReload(currentFilterData!);
      return;
    }

    this.callGenericTabAPIWithReload(currentFilterData, tabType, forceRefresh);
  }

  /** Try to get projectId and projectdataId from current route (e.g. /common-ai-analysis/generic/:projectId?projectdataId=xxx). */
  private getProjectContextFromRouter(): { projectId: string; projectdataId: string } | null {
    try {
      const url = this.router.url;
      const match = url.match(/\/common-ai-analysis\/generic\/([^/?]+)/);
      const projectId = match?.[1]?.trim();
      const q = url.indexOf('?');
      let projectdataId: string | undefined;
      if (q !== -1) {
        const params = new URLSearchParams(url.slice(q));
        projectdataId = params.get('projectdataId')?.trim() || undefined;
      }
      if (projectId && projectdataId) return { projectId, projectdataId };
    } catch {
      // ignore
    }
    return null;
  }

  /**
   * Run only category-analysis tab on backend (dev reload). On completion, fetches updated
   * version and updates category-analysis tab in place (no page redirect).
   */
  private startCategoryAnalysisReload(filterData: FilterData): void {
    const projectData = this.getCurrentProjectData();
    const projectDescription = projectData?.description || '';
    const baseDomain = this.extractBaseDomain(projectDescription);
    const detectedIndustry = this.getCurrentIndustry();
    const projectCategory = projectData?.category ?? (projectData?.categories?.find((c: { isPrimary?: boolean }) => c.isPrimary)?.name);
    const categoryForBulk = projectCategory ?? filterData.category ?? 'General';
    const filtersForBulk: Record<string, unknown> = {
      country: filterData.country,
      brands: filterData.selectedEntities.map((e: any) => e.name),
      competitors: filterData.selectedEntities.slice(1).map((e: any) => e.name),
      category: categoryForBulk,
      industry: detectedIndustry,
      language: projectData?.language || 'English',
      project_created_at: filterData.projectCreatedAt || projectData?.created_at
    };
    if (projectData?.categories?.length) {
      filtersForBulk['categories'] = projectData.categories.map((c: { name: string }) => c.name);
    }
    if ((projectData as any)?.competitors_high?.length) {
      filtersForBulk['competitors_high'] = (projectData as any).competitors_high.map((c: { name: string }) => c.name);
    }
    if ((projectData as any)?.competitors_low?.length) {
      filtersForBulk['competitors_low'] = (projectData as any).competitors_low.map((c: { name: string }) => c.name);
    }
    if (baseDomain) {
      filtersForBulk['base_domain'] = baseDomain;
      filtersForBulk['max_pages'] = 50;
    }
    if (projectDescription) {
      filtersForBulk['project_description'] = projectDescription;
    }

    let projectId = this.currentProjectId || undefined;
    let projectdataId = this.currentProjectdataId || undefined;
    if (!projectId || !projectdataId) {
      const fromRouter = this.getProjectContextFromRouter();
      if (fromRouter) {
        projectId = projectId || fromRouter.projectId || undefined;
        projectdataId = projectdataId || fromRouter.projectdataId || undefined;
      }
    }
    if (!projectId || !projectdataId) {
      console.warn('⚠️ Category analysis reload: no project/projectdata context — result will not be saved to project. Open analysis from a project version URL.');
    } else {
      console.log('📦 Category analysis reload: saving to project', projectId, 'version', projectdataId);
    }
    this.isCategoryAnalysisReloadOnly = true;
    this.categoryAnalysisBackgroundLoadSubject.next({ phase: 'running', taskId: null });
    this.bulkSearchService.startBulkAnalysis(
      projectId ?? undefined,
      projectdataId ?? undefined,
      filtersForBulk,
      false,
      ['category-analysis']
    ).then(({ taskId, totalTabs }) => {
      this.currentTaskIdSubject.next(taskId);
      this.categoryAnalysisBackgroundLoadSubject.next({ phase: 'running', taskId });
      this.globalLoaderService.initializeProgress(totalTabs);
      this.bulkSearchService.startProgressPolling(taskId, (status: TaskStatus) => {
        let currentTabDisplay = status.current_tab || (status.completed_tab_names?.length ? `Completed: ${status.completed_tab_names[status.completed_tab_names.length - 1]}` : `Processing... (${status.completed_tabs}/${status.total_tabs} tabs)`);
        this.globalLoaderService.updateProgress(
          Math.min(status.progress_percentage, 99),
          status.completed_tabs,
          status.completed_tab_names || [],
          currentTabDisplay,
          status.estimated_time_remaining || null,
          status.total_tabs,
          undefined,
          status.elapsed_time ?? null
        );
        if (status.status === 'completed') {
          this.globalLoaderService.resetLoader();
          this.bulkSearchService.stopProgressPolling();
          this.currentTaskIdSubject.next(null);
          this.isApiCallInProgress = false;
          this.isLoadingSubject.next(false);
          this.isCategoryAnalysisReloadOnly = false;
          this.categoryAnalysisBackgroundLoadSubject.next({ phase: 'idle', taskId: null });
          const projectdataIdFromStatus = status.projectdata_id;
          if (projectdataIdFromStatus) {
            // Brief delay so backend has finished persisting (backend now saves before broadcasting, this is a safety net)
            const fetchVersion = () => {
              this.projectHistoryService.getVersionData(projectdataIdFromStatus).subscribe({
                next: (versionData) => {
                  const tabData = versionData?.data?.['category-analysis'];
                  if (tabData) {
                    this.updateCategoryAnalysisResponse(tabData);
                    console.log('✅ Category analysis reload: tab updated and saved to project');
                  }
                },
                error: () => {
                  this.setError('Failed to load updated category analysis');
                }
              });
            };
            setTimeout(fetchVersion, 200);
          } else {
            this.setError('No version ID returned — result may not have been saved. Open the analysis from a project with a version.');
          }
        } else if (status.status === 'failed') {
          this.globalLoaderService.resetLoader();
          this.bulkSearchService.stopProgressPolling();
          this.currentTaskIdSubject.next(null);
          this.isApiCallInProgress = false;
          this.isLoadingSubject.next(false);
          this.isCategoryAnalysisReloadOnly = false;
          this.categoryAnalysisBackgroundLoadSubject.next({ phase: 'idle', taskId: null });
          this.handleBulkAnalysisError(status.error_message || 'Category analysis reload failed');
        }
      });
    }).catch((error) => {
      this.isCategoryAnalysisReloadOnly = false;
      this.categoryAnalysisBackgroundLoadSubject.next({ phase: 'idle', taskId: null });
      this.isApiCallInProgress = false;
      this.isLoadingSubject.next(false);
      this.handleBulkAnalysisError(error?.message || error);
    });
  }

  /**
   * Run only ai-citations tab on backend (same as Category Analysis reload). On completion, fetches updated
   * version and updates ai-citations tab in place (no page redirect).
   */
  private startAiCitationsReload(filterData: FilterData): void {
    const projectData = this.getCurrentProjectData();
    const projectDescription = projectData?.description || '';
    const baseDomain = this.extractBaseDomain(projectDescription);
    const detectedIndustry = this.getCurrentIndustry();
    const projectCategory = projectData?.category ?? (projectData?.categories?.find((c: { isPrimary?: boolean }) => c.isPrimary)?.name);
    const categoryForBulk = projectCategory ?? filterData.category ?? 'General';
    const filtersForBulk: Record<string, unknown> = {
      country: filterData.country,
      brands: filterData.selectedEntities.map((e: any) => e.name),
      competitors: filterData.selectedEntities.slice(1).map((e: any) => e.name),
      category: categoryForBulk,
      industry: detectedIndustry,
      language: projectData?.language || 'English',
      project_created_at: filterData.projectCreatedAt || projectData?.created_at
    };
    if (projectData?.categories?.length) {
      filtersForBulk['categories'] = projectData.categories.map((c: { name: string }) => c.name);
    }
    if ((projectData as any)?.competitors_high?.length) {
      filtersForBulk['competitors_high'] = (projectData as any).competitors_high.map((c: { name: string }) => c.name);
    }
    if ((projectData as any)?.competitors_low?.length) {
      filtersForBulk['competitors_low'] = (projectData as any).competitors_low.map((c: { name: string }) => c.name);
    }
    if (baseDomain) {
      filtersForBulk['base_domain'] = baseDomain;
      filtersForBulk['max_pages'] = 50;
    }
    if (projectDescription) {
      filtersForBulk['project_description'] = projectDescription;
    }

    let projectId = this.currentProjectId || undefined;
    let projectdataId = this.currentProjectdataId || undefined;
    if (!projectId || !projectdataId) {
      const fromRouter = this.getProjectContextFromRouter();
      if (fromRouter) {
        projectId = projectId || fromRouter.projectId || undefined;
        projectdataId = projectdataId || fromRouter.projectdataId || undefined;
      }
    }
    if (!projectId || !projectdataId) {
      console.warn('⚠️ AI Citations reload: no project/projectdata context — result will not be saved to project. Open analysis from a project version URL.');
    } else {
      console.log('📦 AI Citations reload: saving to project', projectId, 'version', projectdataId);
    }
    this.bulkSearchService.startBulkAnalysis(
      projectId ?? undefined,
      projectdataId ?? undefined,
      filtersForBulk,
      false,
      ['ai-citations']
    ).then(({ taskId, totalTabs }) => {
      this.currentTaskIdSubject.next(taskId);
      this.globalLoaderService.initializeProgress(totalTabs);
      this.bulkSearchService.startProgressPolling(taskId, (status: TaskStatus) => {
        const currentTabDisplay = status.current_tab || (status.completed_tab_names?.length ? `Completed: ${status.completed_tab_names[status.completed_tab_names.length - 1]}` : `Processing... (${status.completed_tabs}/${status.total_tabs} tabs)`);
        this.globalLoaderService.updateProgress(
          Math.min(status.progress_percentage, 99),
          status.completed_tabs,
          status.completed_tab_names || [],
          currentTabDisplay,
          status.estimated_time_remaining || null,
          status.total_tabs,
          undefined,
          status.elapsed_time ?? null
        );
        if (status.status === 'completed') {
          this.globalLoaderService.resetLoader();
          this.bulkSearchService.stopProgressPolling();
          this.currentTaskIdSubject.next(null);
          this.isApiCallInProgress = false;
          this.isLoadingSubject.next(false);
          const projectdataIdFromStatus = status.projectdata_id;
          if (projectdataIdFromStatus) {
            const fetchVersion = () => {
              this.projectHistoryService.getVersionData(projectdataIdFromStatus).subscribe({
                next: (versionData) => {
                  const tabData = versionData?.data?.['ai-citations'];
                  if (tabData) {
                    this.updateAiCitationsResponse(tabData);
                    console.log('✅ AI Citations reload: tab updated and saved to project');
                  }
                },
                error: () => {
                  this.setError('Failed to load updated AI Citations');
                }
              });
            };
            setTimeout(fetchVersion, 200);
          } else {
            this.setError('No version ID returned — result may not have been saved. Open the analysis from a project with a version.');
          }
        } else if (status.status === 'failed') {
          this.globalLoaderService.resetLoader();
          this.bulkSearchService.stopProgressPolling();
          this.currentTaskIdSubject.next(null);
          this.isApiCallInProgress = false;
          this.isLoadingSubject.next(false);
          this.handleBulkAnalysisError(status.error_message || 'AI Citations reload failed');
        }
      });
    }).catch((error) => {
      this.isApiCallInProgress = false;
      this.isLoadingSubject.next(false);
      this.handleBulkAnalysisError(error?.message || error);
    });
  }

  /**
   * Run only ai-visibility tab on backend (RAG-based scoring). On completion, fetches updated
   * version (overview with merged AI visibility metrics) and updates overview in place.
   */
  private startAiVisibilityReload(filterData: FilterData): void {
    const projectData = this.getCurrentProjectData();
    const projectDescription = projectData?.description || '';
    const baseDomain = this.extractBaseDomain(projectDescription);
    const detectedIndustry = this.getCurrentIndustry();
    const projectCategory = projectData?.category ?? (projectData?.categories?.find((c: { isPrimary?: boolean }) => c.isPrimary)?.name);
    const categoryForBulk = projectCategory ?? filterData.category ?? 'General';
    const filtersForBulk: Record<string, unknown> = {
      country: filterData.country,
      brands: filterData.selectedEntities.map((e: any) => e.name),
      competitors: filterData.selectedEntities.slice(1).map((e: any) => e.name),
      category: categoryForBulk,
      industry: detectedIndustry,
      language: projectData?.language || 'English',
      project_created_at: filterData.projectCreatedAt || projectData?.created_at
    };
    if (projectData?.categories?.length) {
      filtersForBulk['categories'] = projectData.categories.map((c: { name: string }) => c.name);
    }
    if ((projectData as any)?.competitors_high?.length) {
      filtersForBulk['competitors_high'] = (projectData as any).competitors_high.map((c: { name: string }) => c.name);
    }
    if ((projectData as any)?.competitors_low?.length) {
      filtersForBulk['competitors_low'] = (projectData as any).competitors_low.map((c: { name: string }) => c.name);
    }
    if (baseDomain) {
      filtersForBulk['base_domain'] = baseDomain;
      filtersForBulk['max_pages'] = 50;
    }
    if (projectDescription) {
      filtersForBulk['project_description'] = projectDescription;
    }

    let projectId = this.currentProjectId || undefined;
    let projectdataId = this.currentProjectdataId || undefined;
    if (!projectId || !projectdataId) {
      const fromRouter = this.getProjectContextFromRouter();
      if (fromRouter) {
        projectId = projectId || fromRouter.projectId || undefined;
        projectdataId = projectdataId || fromRouter.projectdataId || undefined;
      }
    }
    if (!projectId || !projectdataId) {
      console.warn('⚠️ AI Visibility reload: no project/projectdata context — result will not be saved. Open analysis from a project version URL.');
    } else {
      console.log('📦 AI Visibility reload: saving to project', projectId, 'version', projectdataId);
    }
    this.isApiCallInProgress = true;
    this.setLoading(true);
    this.setError(null);
    this.bulkSearchService.startBulkAnalysis(
      projectId ?? undefined,
      projectdataId ?? undefined,
      filtersForBulk,
      false,
      ['ai-visibility']
    ).then(({ taskId, totalTabs }) => {
      this.currentTaskIdSubject.next(taskId);
      this.globalLoaderService.initializeProgress(totalTabs);
      this.bulkSearchService.startProgressPolling(taskId, (status: TaskStatus) => {
        const currentTabDisplay = status.current_tab || (status.completed_tab_names?.length ? `Completed: ${status.completed_tab_names[status.completed_tab_names.length - 1]}` : `Processing... (${status.completed_tabs}/${status.total_tabs} tabs)`);
        this.globalLoaderService.updateProgress(
          Math.min(status.progress_percentage, 99),
          status.completed_tabs,
          status.completed_tab_names || [],
          currentTabDisplay,
          status.estimated_time_remaining || null,
          status.total_tabs,
          undefined,
          status.elapsed_time ?? null
        );
        if (status.status === 'completed') {
          this.globalLoaderService.resetLoader();
          this.bulkSearchService.stopProgressPolling();
          this.currentTaskIdSubject.next(null);
          this.isApiCallInProgress = false;
          this.isLoadingSubject.next(false);
          const projectdataIdFromStatus = status.projectdata_id;
          if (projectdataIdFromStatus) {
            const fetchVersion = () => {
              this.projectHistoryService.getVersionData(projectdataIdFromStatus).subscribe({
                next: (versionData) => {
                  const opb = versionData?.data?.['overview-prompts-build'];
                  const overview = versionData?.data?.['overview'];
                  const tabData = opb || overview;
                  if (tabData) {
                    this.updateAnalysisResponse(tabData);
                    console.log('✅ AI Visibility reload: overview updated with RAG metrics and saved to project');
                  }
                },
                error: () => {
                  this.setError('Failed to load updated overview after AI Visibility reload');
                }
              });
            };
            setTimeout(fetchVersion, 200);
          } else {
            this.setError('No version ID returned — result may not have been saved. Open the analysis from a project with a version.');
          }
        } else if (status.status === 'failed') {
          this.globalLoaderService.resetLoader();
          this.bulkSearchService.stopProgressPolling();
          this.currentTaskIdSubject.next(null);
          this.isApiCallInProgress = false;
          this.isLoadingSubject.next(false);
          this.handleBulkAnalysisError(status.error_message || 'AI Visibility reload failed');
        }
      });
    }).catch((error) => {
      this.isApiCallInProgress = false;
      this.isLoadingSubject.next(false);
      this.handleBulkAnalysisError(error?.message || error);
    });
  }

  /**
   * Run only overview tab on backend (dev reload). On completion, fetches updated
   * version and updates overview tab in place (no page redirect).
   */
  private startOverviewReload(filterData: FilterData): void {
    const projectData = this.getCurrentProjectData();
    const projectDescription = projectData?.description || '';
    const baseDomain = this.extractBaseDomain(projectDescription);
    const detectedIndustry = this.getCurrentIndustry();
    const projectCategory = projectData?.category ?? (projectData?.categories?.find((c: { isPrimary?: boolean }) => c.isPrimary)?.name);
    const categoryForBulk = projectCategory ?? filterData.category ?? 'General';
    const filtersForBulk: Record<string, unknown> = {
      country: filterData.country,
      brands: filterData.selectedEntities.map((e: any) => e.name),
      competitors: filterData.selectedEntities.slice(1).map((e: any) => e.name),
      category: categoryForBulk,
      industry: detectedIndustry,
      language: projectData?.language || 'English',
      project_created_at: filterData.projectCreatedAt || projectData?.created_at
    };
    if (projectData?.categories?.length) {
      filtersForBulk['categories'] = projectData.categories.map((c: { name: string }) => c.name);
    }
    if ((projectData as any)?.competitors_high?.length) {
      filtersForBulk['competitors_high'] = (projectData as any).competitors_high.map((c: { name: string }) => c.name);
    }
    if ((projectData as any)?.competitors_low?.length) {
      filtersForBulk['competitors_low'] = (projectData as any).competitors_low.map((c: { name: string }) => c.name);
    }
    if (baseDomain) {
      filtersForBulk['base_domain'] = baseDomain;
      filtersForBulk['max_pages'] = 50;
    }
    if (projectDescription) {
      filtersForBulk['project_description'] = projectDescription;
    }

    let projectId = this.currentProjectId || undefined;
    let projectdataId = this.currentProjectdataId || undefined;
    if (!projectId || !projectdataId) {
      const fromRouter = this.getProjectContextFromRouter();
      if (fromRouter) {
        projectId = projectId || fromRouter.projectId || undefined;
        projectdataId = projectdataId || fromRouter.projectdataId || undefined;
      }
    }
    if (!projectId || !projectdataId) {
      console.warn('⚠️ Overview reload: no project/projectdata context — result will not be saved to project. Open analysis from a project version URL.');
    } else {
      console.log('📦 Overview reload: saving to project', projectId, 'version', projectdataId);
    }
    this.isOverviewReloadOnly = true;
    this.bulkSearchService.startBulkAnalysis(
      projectId ?? undefined,
      projectdataId ?? undefined,
      filtersForBulk,
      false,
      ['overview']
    ).then(({ taskId, totalTabs }) => {
      this.currentTaskIdSubject.next(taskId);
      this.globalLoaderService.initializeProgress(totalTabs);
      this.bulkSearchService.startProgressPolling(taskId, (status: TaskStatus) => {
        const currentTabDisplay = status.current_tab || (status.completed_tab_names?.length ? `Completed: ${status.completed_tab_names[status.completed_tab_names.length - 1]}` : `Processing... (${status.completed_tabs}/${status.total_tabs} tabs)`);
        this.globalLoaderService.updateProgress(
          Math.min(status.progress_percentage, 99),
          status.completed_tabs,
          status.completed_tab_names || [],
          currentTabDisplay,
          status.estimated_time_remaining || null,
          status.total_tabs,
          undefined,
          status.elapsed_time ?? null
        );
        if (status.status === 'completed') {
          this.globalLoaderService.resetLoader();
          this.bulkSearchService.stopProgressPolling();
          this.currentTaskIdSubject.next(null);
          this.isApiCallInProgress = false;
          this.isLoadingSubject.next(false);
          this.isOverviewReloadOnly = false;
          const projectdataIdFromStatus = status.projectdata_id;
          if (projectdataIdFromStatus) {
            const fetchVersion = () => {
              this.projectHistoryService.getVersionData(projectdataIdFromStatus).subscribe({
                next: (versionData) => {
                  const tabData = versionData?.data?.['overview'];
                  if (tabData) {
                    this.updateAnalysisResponse(tabData);
                    console.log('✅ Overview reload: tab updated and saved to project');
                  }
                },
                error: () => {
                  this.setError('Failed to load updated overview');
                }
              });
            };
            setTimeout(fetchVersion, 200);
          } else {
            this.setError('No version ID returned — result may not have been saved. Open the analysis from a project with a version.');
          }
        } else if (status.status === 'failed') {
          this.globalLoaderService.resetLoader();
          this.bulkSearchService.stopProgressPolling();
          this.currentTaskIdSubject.next(null);
          this.isApiCallInProgress = false;
          this.isLoadingSubject.next(false);
          this.isOverviewReloadOnly = false;
          this.handleBulkAnalysisError(status.error_message || 'Overview reload failed');
        }
      });
    }).catch((error) => {
      this.isOverviewReloadOnly = false;
      this.isApiCallInProgress = false;
      this.isLoadingSubject.next(false);
      this.handleBulkAnalysisError(error?.message || error);
    });
  }

  /**
   * Run only ai-content-index tab on backend (same as Overview / Category Analysis reload). On completion, fetches updated
   * version and updates ai-content-index tab in place (no page redirect).
   */
  private startAiContentIndexReload(filterData: FilterData): void {
    const projectData = this.getCurrentProjectData();
    const projectDescription = projectData?.description || '';
    const baseDomain = this.extractBaseDomain(projectDescription);
    const detectedIndustry = this.getCurrentIndustry();
    const projectCategory = projectData?.category ?? (projectData?.categories?.find((c: { isPrimary?: boolean }) => c.isPrimary)?.name);
    const categoryForBulk = projectCategory ?? filterData.category ?? 'General';
    const filtersForBulk: Record<string, unknown> = {
      country: filterData.country,
      brands: filterData.selectedEntities.map((e: any) => e.name),
      competitors: filterData.selectedEntities.slice(1).map((e: any) => e.name),
      category: categoryForBulk,
      industry: detectedIndustry,
      language: projectData?.language || 'English',
      project_created_at: filterData.projectCreatedAt || projectData?.created_at
    };
    if (projectData?.categories?.length) {
      filtersForBulk['categories'] = projectData.categories.map((c: { name: string }) => c.name);
    }
    if ((projectData as any)?.competitors_high?.length) {
      filtersForBulk['competitors_high'] = (projectData as any).competitors_high.map((c: { name: string }) => c.name);
    }
    if ((projectData as any)?.competitors_low?.length) {
      filtersForBulk['competitors_low'] = (projectData as any).competitors_low.map((c: { name: string }) => c.name);
    }
    if (baseDomain) {
      filtersForBulk['base_domain'] = baseDomain;
      filtersForBulk['max_pages'] = 50;
    }
    if (projectDescription) {
      filtersForBulk['project_description'] = projectDescription;
    }

    let projectId = this.currentProjectId || undefined;
    let projectdataId = this.currentProjectdataId || undefined;
    if (!projectId || !projectdataId) {
      const fromRouter = this.getProjectContextFromRouter();
      if (fromRouter) {
        projectId = projectId || fromRouter.projectId || undefined;
        projectdataId = projectdataId || fromRouter.projectdataId || undefined;
      }
    }
    if (!projectId || !projectdataId) {
      console.warn('⚠️ AI Content Index reload: no project/projectdata context — result will not be saved to project. Open analysis from a project version URL.');
    } else {
      console.log('📦 AI Content Index reload: saving to project', projectId, 'version', projectdataId);
    }
    this.bulkSearchService.startBulkAnalysis(
      projectId ?? undefined,
      projectdataId ?? undefined,
      filtersForBulk,
      false,
      ['ai-content-index']
    ).then(({ taskId, totalTabs }) => {
      this.currentTaskIdSubject.next(taskId);
      this.globalLoaderService.initializeProgress(totalTabs);
      this.bulkSearchService.startProgressPolling(taskId, (status: TaskStatus) => {
        const currentTabDisplay = status.current_tab || (status.completed_tab_names?.length ? `Completed: ${status.completed_tab_names[status.completed_tab_names.length - 1]}` : `Processing... (${status.completed_tabs}/${status.total_tabs} tabs)`);
        this.globalLoaderService.updateProgress(
          Math.min(status.progress_percentage, 99),
          status.completed_tabs,
          status.completed_tab_names || [],
          currentTabDisplay,
          status.estimated_time_remaining || null,
          status.total_tabs
        );
        if (status.status === 'completed') {
          this.globalLoaderService.resetLoader();
          this.bulkSearchService.stopProgressPolling();
          this.currentTaskIdSubject.next(null);
          this.isApiCallInProgress = false;
          this.isLoadingSubject.next(false);
          const projectdataIdFromStatus = status.projectdata_id;
          if (projectdataIdFromStatus) {
            const fetchVersion = () => {
              this.projectHistoryService.getVersionData(projectdataIdFromStatus).subscribe({
                next: (versionData) => {
                  const tabData = versionData?.data?.['ai-content-index'];
                  if (tabData) {
                    this.updateAiContentIndexResponse(tabData);
                    console.log('✅ AI Content Index reload: tab updated and saved to project');
                  }
                },
                error: () => {
                  this.setError('Failed to load updated AI Content Index');
                }
              });
            };
            setTimeout(fetchVersion, 200);
          } else {
            this.setError('No version ID returned — result may not have been saved. Open the analysis from a project with a version.');
          }
        } else if (status.status === 'failed') {
          this.globalLoaderService.resetLoader();
          this.bulkSearchService.stopProgressPolling();
          this.currentTaskIdSubject.next(null);
          this.isApiCallInProgress = false;
          this.isLoadingSubject.next(false);
          this.handleBulkAnalysisError(status.error_message || 'AI Content Index reload failed');
        }
      });
    }).catch((error) => {
      this.isApiCallInProgress = false;
      this.isLoadingSubject.next(false);
      this.handleBulkAnalysisError(error?.message || error);
    });
  }

  /**
   * Generic single-tab reload for tabs that follow the standard update pattern.
   */
  private startStandardSingleTabReload(filterData: FilterData, tabName: 'sentiment-tracking' | 'consumer-insights' | 'content-creation' | 'performance-tracking'): void {
    const projectData = this.getCurrentProjectData();
    const projectDescription = projectData?.description || '';
    const baseDomain = this.extractBaseDomain(projectDescription);
    const detectedIndustry = this.getCurrentIndustry();
    const projectCategory = projectData?.category ?? (projectData?.categories?.find((c: { isPrimary?: boolean }) => c.isPrimary)?.name);
    const categoryForBulk = projectCategory ?? filterData.category ?? 'General';

    const filtersForBulk: Record<string, unknown> = {
      country: filterData.country,
      brands: filterData.selectedEntities.map((e: any) => e.name),
      competitors: filterData.selectedEntities.slice(1).map((e: any) => e.name),
      category: categoryForBulk,
      industry: detectedIndustry,
      language: projectData?.language || 'English',
      project_created_at: filterData.projectCreatedAt || projectData?.created_at
    };
    if (projectData?.categories?.length) {
      filtersForBulk['categories'] = projectData.categories.map((c: { name: string }) => c.name);
    }
    if ((projectData as any)?.competitors_high?.length) {
      filtersForBulk['competitors_high'] = (projectData as any).competitors_high.map((c: { name: string }) => c.name);
    }
    if ((projectData as any)?.competitors_low?.length) {
      filtersForBulk['competitors_low'] = (projectData as any).competitors_low.map((c: { name: string }) => c.name);
    }
    if (baseDomain) {
      filtersForBulk['base_domain'] = baseDomain;
      filtersForBulk['max_pages'] = 50;
    }
    if (projectDescription) {
      filtersForBulk['project_description'] = projectDescription;
    }

    let projectId = this.currentProjectId || undefined;
    let projectdataId = this.currentProjectdataId || undefined;
    if (!projectId || !projectdataId) {
      const fromRouter = this.getProjectContextFromRouter();
      if (fromRouter) {
        projectId = projectId || fromRouter.projectId || undefined;
        projectdataId = projectdataId || fromRouter.projectdataId || undefined;
      }
    }
    if (!projectId || !projectdataId) {
      console.warn(`⚠️ ${tabName} reload: no project/projectdata context — result may not be saved to project. Open analysis from a project version URL.`);
    } else {
      console.log(`📦 ${tabName} reload: saving to project`, projectId, 'version', projectdataId);
    }

    this.bulkSearchService.startBulkAnalysis(
      projectId ?? undefined,
      projectdataId ?? undefined,
      filtersForBulk,
      false,
      [tabName]
    ).then(({ taskId, totalTabs }) => {
      this.currentTaskIdSubject.next(taskId);
      this.globalLoaderService.initializeProgress(totalTabs);
      this.bulkSearchService.startProgressPolling(taskId, (status: TaskStatus) => {
        const currentTabDisplay = status.current_tab || (status.completed_tab_names?.length ? `Completed: ${status.completed_tab_names[status.completed_tab_names.length - 1]}` : `Processing... (${status.completed_tabs}/${status.total_tabs} tabs)`);
        this.globalLoaderService.updateProgress(
          Math.min(status.progress_percentage, 99),
          status.completed_tabs,
          status.completed_tab_names || [],
          currentTabDisplay,
          status.estimated_time_remaining || null,
          status.total_tabs,
          undefined,
          status.elapsed_time ?? null
        );
        if (status.status === 'completed') {
          this.globalLoaderService.resetLoader();
          this.bulkSearchService.stopProgressPolling();
          this.currentTaskIdSubject.next(null);
          this.isApiCallInProgress = false;
          this.isLoadingSubject.next(false);
          const projectdataIdFromStatus = status.projectdata_id;
          if (projectdataIdFromStatus) {
            const fetchVersion = () => {
              this.projectHistoryService.getVersionData(projectdataIdFromStatus).subscribe({
                next: (versionData) => {
                  const tabData = versionData?.data?.[tabName];
                  if (tabData) {
                    this.routeResponseToTab(tabName, tabData);
                    console.log(`✅ ${tabName} reload: tab updated and saved to project`);
                  }
                },
                error: () => {
                  this.setError(`Failed to load updated ${tabName}`);
                }
              });
            };
            setTimeout(fetchVersion, 200);
          } else {
            this.setError('No version ID returned — result may not have been saved. Open the analysis from a project with a version.');
          }
        } else if (status.status === 'failed') {
          this.globalLoaderService.resetLoader();
          this.bulkSearchService.stopProgressPolling();
          this.currentTaskIdSubject.next(null);
          this.isApiCallInProgress = false;
          this.isLoadingSubject.next(false);
          this.handleBulkAnalysisError(status.error_message || `${tabName} reload failed`);
        }
      });
    }).catch((error) => {
      this.isApiCallInProgress = false;
      this.isLoadingSubject.next(false);
      this.handleBulkAnalysisError(error?.message || error);
    });
  }

  private callGenericTabAPI(filterData: FilterData | null, tabType: string): void {
    console.log(`📞 Calling generic tab API for ${tabType} with filter data:`, filterData);
    
    const selectedEntities = filterData?.selectedEntities || [];
    const country = filterData?.country || 'Global';
    const projectCreatedAt = filterData?.projectCreatedAt;
    const primaryBrand = selectedEntities[0]?.name;

    let industry = 'General';
    if (primaryBrand) {
      const cachedIndustry = this.industryCacheSubject.value.get(primaryBrand);
      if (cachedIndustry) {
        industry = cachedIndustry;
      }
    }

    console.log(`📊 Making API call for ${tabType}:`, {
      selectedEntities: selectedEntities.map(e => e.name),
      industry,
      country,
      projectCreatedAt
    });

    this.makeTabAPICall(selectedEntities, industry, country, projectCreatedAt, tabType, false);
  }

  private makeTabAPICall(selectedEntities: any[], industry: string, country: string, projectCreatedAt: string | undefined, tabType: string, forceRefresh: boolean): void {
    // DISABLED: Don't make simple-search API calls - data should come from history or bulk search
    console.log(`⏭️ Skipping simple-search API call for ${tabType} - use history data or bulk search instead`);
    this.isLoadingSubject.next(false);
    this.isApiCallInProgress = false;
    this.tabApiCallInProgress.delete(tabType);
    
    // Check if data already exists for this tab
    const existingResponse = this.getExistingResponseForTab(tabType);
    if (existingResponse) {
      console.log(`✅ Using existing data for ${tabType}`);
      this.routeResponseToTab(tabType, existingResponse);
    } else {
      console.log(`⚠️ No existing data found for ${tabType} - data should be loaded from history`);
    }
    return;
  }

  private callGenericTabAPIWithReload(filterData: FilterData | null, tabType: string, forceRefresh: boolean): void {
    // REMOVED: No API calls - use existing data from history or bulk-search
    console.log(`⏭️ Skipping API call for ${tabType} - use existing data`);
    
    // Check if data already exists for this tab
    const existingResponse = this.getExistingResponseForTab(tabType);
    if (existingResponse) {
      console.log(`✅ Using existing data for ${tabType}`);
      this.routeResponseToTab(tabType, existingResponse);
      
      // If this is Overview after bulk analysis, update progress to 100% and finalize
      if (tabType === 'overview' && this.isBulkSearchWithOverviewInProgress) {
        console.log('📊 Overview data loaded, updating progress to 100%');
        this.globalLoaderService.updateProgressPercentage(100);
        // Give a brief moment for progress update to render before finalizing
        setTimeout(() => {
          this.finalizeBulkSearchWithOverview();
        }, 300);
      }
    } else {
      console.log(`⚠️ No existing data found for ${tabType}`);
      // If this is Overview and no data found, still finalize the bulk search
      if (tabType === 'overview' && this.isBulkSearchWithOverviewInProgress) {
        console.log('⚠️ No Overview data found, finalizing bulk search');
        this.finalizeBulkSearchWithOverview();
      }
    }
  }

  private callGenericOverviewAPIWithReload(filterData: FilterData, forceRefresh: boolean): void {
    this.callGenericTabAPIWithReload(filterData, 'overview', forceRefresh);
  }

  getCurrentFilterData(): FilterData | null {
    return this.filterDataSubject.value;
  }

  getCurrentProjectData(): Project | null {
    return this.projectDataSubject.value;
  }

  getExistingResponseForTab(tabType: string): any | null {
    switch (tabType) {
      case 'overview':
        return this.analysisResponseSubject.value;
      case 'prompts':
        return this.analysisResponseSubject.value;
      case 'ai-citations':
        return this.aiCitationsResponseSubject.value;
      case 'category-analysis':
        return this.categoryAnalysisResponseSubject.value;
      case 'sentiment-tracking':
        return this.sentimentTrackingResponseSubject.value;
      case 'ai-content-index':
        return this.aiContentIndexResponseSubject.value;
      case 'performance-tracking':
        return this.performanceTrackingResponseSubject.value;
      case 'consumer-insights':
        return this.consumerInsightsResponseSubject.value;
      case 'content-creation':
        return this.contentCreationResponseSubject.value;
      case 'eeat-audit':
        return this.eeatAuditResponseSubject.value;
      case 'landing-page-audit':
        return this.landingPageAuditResponseSubject.value;
      case 'ai-audit':
        return this.aiAuditResponseSubject.value;
      default:
        return null;
    }
  }

  getCurrentAnalysisResponse(): any | null {
    return this.analysisResponseSubject.value;
  }

  getCurrentAiCitationsResponse(): any | null {
    return this.aiCitationsResponseSubject.value;
  }

  getCurrentCategoryAnalysisResponse(): any | null {
    return this.categoryAnalysisResponseSubject.value;
  }

  getCurrentSentimentTrackingResponse(): any | null {
    return this.sentimentTrackingResponseSubject.value;
  }

  getCurrentAiContentIndexResponse(): any | null {
    return this.aiContentIndexResponseSubject.value;
  }

  getCurrentPerformanceTrackingResponse(): any | null {
    return this.performanceTrackingResponseSubject.value;
  }

  getCurrentContentCreationResponse(): any | null {
    return this.contentCreationResponseSubject.value;
  }

  getCurrentEeatAuditResponse(): any | null {
    return this.eeatAuditResponseSubject.value;
  }

  getCurrentLandingPageAuditResponse(): any | null {
    return this.landingPageAuditResponseSubject.value;
  }

  getCurrentAIAuditResponse(): any | null {
    return this.aiAuditResponseSubject.value;
  }

  getContentCreationResponse$(): any {
    return this.contentCreationResponse$;
  }

  getCurrentConsumerInsightsResponse(): any | null {
    return this.consumerInsightsResponseSubject.value;
  }

  getConsumerInsightsResponse$(): any {
    return this.consumerInsightsResponse$;
  }

  getCurrentTabFlow(): TabFlow {
    return this.tabFlowSubject.value;
  }

  getCachedIndustry(brandName: string): string | null {
    // Clean brand name by removing (YOU) suffix to match cache key
    const cleanedBrandName = brandName.replace(/\s*\(YOU\)\s*/g, '').trim();
    return this.industryCacheSubject.value.get(cleanedBrandName) || null;
  }

  clearIndustryCache(): void {
    this.industryCacheSubject.next(new Map());
  }

  clearIndustryCacheForBrand(brandName: string): void {
    const currentCache = this.industryCacheSubject.value;
    currentCache.delete(brandName);
    this.industryCacheSubject.next(new Map(currentCache));
  }


  private routeResponseToTab(tabType: string, response: any): void {
    switch (tabType) {
      case 'overview':
        this.updateAnalysisResponse(response);
        break;
      case 'prompts':
        this.updateAnalysisResponse(response);
        break;
      case 'ai-citations':
        this.updateAiCitationsResponse(response);
        break;
      case 'category-analysis':
        // Do not overwrite with minimal payload (Bulk returns minimal; data comes from AI Visibility / history).
        if (this.hasRealCategoryAnalysisData(response)) {
          this.updateCategoryAnalysisResponse(response);
        }
        break;
      case 'sentiment-tracking':
        this.updateSentimentTrackingResponse(response);
        break;
      case 'ai-content-index':
        this.updateAiContentIndexResponse(response);
        break;
      case 'performance-tracking':
        this.updatePerformanceTrackingResponse(response);
        break;
      case 'consumer-insights':
        this.updateConsumerInsightsResponse(response);
        break;
      case 'content-creation':
        this.updateContentCreationResponse(response);
        break;
      case 'eeat-audit':
        this.updateEeatAuditResponse(response);
        break;
      case 'landing-page-audit':
        this.updateLandingPageAuditResponse(response);
        break;
      case 'ai-audit':
        this.updateAIAuditResponse(response);
        break;
    }
  }


  clearData(): void {
    this.filterDataSubject.next(null);
    this.analysisResponseSubject.next(null);
    this.isLoadingSubject.next(false);
    this.setError(null);
    this.setCompareClicked(false);
  }

  clearTabCache(tabType: string): void {
    switch (tabType) {
      case 'overview':
        this.analysisResponseSubject.next(null);
        break;
      case 'ai-citations':
        this.aiCitationsResponseSubject.next(null);
        break;
      case 'category-analysis':
        this.categoryAnalysisResponseSubject.next(null);
        break;
      case 'sentiment-tracking':
        this.sentimentTrackingResponseSubject.next(null);
        break;
      case 'ai-content-index':
        this.aiContentIndexResponseSubject.next(null);
        break;
      case 'performance-tracking':
        this.performanceTrackingResponseSubject.next(null);
        break;
      case 'consumer-insights':
        this.consumerInsightsResponseSubject.next(null);
        break;
      case 'content-creation':
        this.contentCreationResponseSubject.next(null);
        break;
    }
  }

  clearAllTabCaches(): void {
    this.analysisResponseSubject.next(null);
    this.aiCitationsResponseSubject.next(null);
    this.categoryAnalysisResponseSubject.next(null);
    this.sentimentTrackingResponseSubject.next(null);
    this.aiContentIndexResponseSubject.next(null);
    this.performanceTrackingResponseSubject.next(null);
    this.consumerInsightsResponseSubject.next(null);
    this.contentCreationResponseSubject.next(null);
  }

  /**
   * Clear all caches and force reload all tabs
   */
  clearAllCachesAndReload(): void {
    console.log('🔄 Clearing all caches and forcing reload...');
    this.clearAllTabCaches();
    this.clearIndustryCache();
    this.setError(null);
    this.setLoading(false);
    this.isApiCallInProgress = false;
    this.isProcessingData = false;
    
    // Force reload all tabs
    const currentFilterData = this.getCurrentFilterData();
    if (currentFilterData) {
      this.startBulkAnalysisWithOverview(currentFilterData);
    }
  }

  resetFilterData(): void {
    this.filterDataSubject.next(null);
  }

  isFilterDataInitialized(): boolean {
    const currentData = this.filterDataSubject.value;
    if (!currentData || !currentData.selectedEntities || currentData.selectedEntities.length === 0 || !currentData.country) {
      return false;
    }

    const currentTabFlow = this.getCurrentTabFlow();

    if (currentTabFlow.type === 'education' || currentTabFlow.type === 'education-university') {
      return !!currentData.category;
    } else {
      return true;
    }
  }


  private extractIndustryFromResponse(response: string): string {
    let industry = response.trim();
    
    industry = industry
      .replace(/industry/gi, '')
      .replace(/sector/gi, '')
      .replace(/the /gi, '')
      .replace(/^"|"$/g, '')
      .trim();
    
    industry = industry.charAt(0).toUpperCase() + industry.slice(1).toLowerCase();
    
    if (!industry || industry.length > 30) {
      return 'General';
    }
    
    return industry;
  }

  /**
   * Check if bulk search is in progress
   */
  isBulkSearchInProgress(): boolean {
    return this.bulkSearchService.isBulkSearchInProgress();
  }

  /**
   * Get current bulk search progress
   */
  getCurrentBulkSearchProgress(): TaskStatus | null {
    return this.bulkSearchProgressSubject.value;
  }

  /**
   * Stop bulk search if in progress
   */
  stopBulkSearch(): void {
    if (this.bulkSearchService.isBulkSearchInProgress()) {
      this.bulkSearchService.stopProgressPolling();
    this.isLoadingSubject.next(false);
    this.bulkSearchProgressSubject.next(null);
    }
  }

  /**
   * Generate cache key for tab analysis
   * Removes (YOU) suffix from brand names to ensure proper caching
   * For performance-tracking and content-creation: only includes your brand (first brand) in cache key
   * For other tabs: includes all entities in cache key
   */
  private generateCacheKey(tabType: string, selectedEntities: any[], industry: string, country: string, projectCreatedAt?: string): string {
    // Clean entity names by removing (YOU) suffix
    let cleanedEntityNames: string;
    const useFirstBrandOnly = tabType === 'performance-tracking' || tabType === 'content-creation';
    
    // For performance-tracking and content-creation, only use the first brand (your brand) in cache key
    if (useFirstBrandOnly) {
      const yourBrand = selectedEntities
        .map(entity => (entity.name || entity).replace(/\s*\(YOU\)\s*/g, '').trim())
        .find(name => name) || selectedEntities[0]?.name || selectedEntities[0] || 'your_brand';
      cleanedEntityNames = yourBrand;
    } else {
      // For other tabs, use all entities
      cleanedEntityNames = selectedEntities
        .map(entity => (entity.name || entity).replace(/\s*\(YOU\)\s*/g, '').trim())
        .sort()
        .join('_');
    }
    
    const normalizedIndustry = industry?.toLowerCase().replace(/\s+/g, '_') || 'general';
    const normalizedCountry = country?.toLowerCase().replace(/\s+/g, '_') || 'global';
    
    let projectDate: Date = new Date();
    // let projectDate: Date;
    // if (projectCreatedAt) {
    //   // Ensure consistent date parsing
    //   projectDate = new Date(projectCreatedAt);
    //   // Validate the date
    //   if (isNaN(projectDate.getTime())) {
    //     console.warn(`⚠️ Invalid projectCreatedAt date: ${projectCreatedAt}, using current date`);
    //     projectDate = new Date();
    //   }
    // } else {
    //   projectDate = new Date();
    // }
    
    const year = projectDate.getFullYear();
    const month = (projectDate.getMonth() + 1).toString().padStart(2, '0');
    const projectYearMonth = `${year}-${month}`;
    
    // Debug logging to track month discrepancy
    console.log(`🔍 Cache Key Debug: projectCreatedAt=${projectCreatedAt}, parsedDate=${projectDate.toISOString()}, month=${month}, projectYearMonth=${projectYearMonth}`);
    
    const cacheKey = `${tabType}_${cleanedEntityNames}_${normalizedIndustry}_${normalizedCountry}_${projectYearMonth}`;
    
    // Debug logging for single-brand cache key tabs
    if (useFirstBrandOnly) {
      console.log(`🔍 ${tabType} Cache Key: ${cacheKey.toLowerCase()} (only your brand: ${cleanedEntityNames})`);
    }
    
    return cacheKey.toLowerCase();
  }

  /**
   * Generate cache key for industry detection
   */
  private generateIndustryCacheKey(brandName: string): string {
    // Clean brand name by removing (YOU) suffix
    const cleanedBrand = brandName.replace(/\s*\(YOU\)\s*/g, '').trim();
    const normalizedBrand = cleanedBrand?.toLowerCase().replace(/\s+/g, '_') || 'unknown';
    return `industry_detection_${normalizedBrand}`;
  }

  /**
   * Start bulk analysis with overview sequence
   */
  private startBulkAnalysisWithOverview(filterData: FilterData, forceRefresh: boolean = false): void {
    console.log('🚀 Starting bulk analysis with overview sequence');
    
    // Set bulk search flag
    this.isBulkSearchWithOverviewInProgress = true;

    // Clear potentially stale data so tabs don't reuse previous responses
    // Especially important for 'overview' and 'ai-citations' which users often revisit immediately
    this.clearTabCache('overview');
    this.clearTabCache('ai-citations');
    this.setError(null);
    
    // Global loader is already initialized in triggerAnalysis when useBulkSearch=true
    // Just ensure it's visible and at 0%
    this.globalLoaderService.updateProgressPercentage(0);
    
    // Prepare filters for bulk search (backend builds all tabs and prompts from filters)
      const projectData = this.getCurrentProjectData();
      const projectDescription = projectData?.description || '';
      const baseDomain = this.extractBaseDomain(projectDescription);
      
      // Prefer project industry/category so backend does not re-detect (AI Content Index and others use filters as-is)
      const projectIndustry = projectData?.industry ?? null;
      const detectedIndustry = this.getCurrentIndustry();
      const industryForBulk = (projectIndustry && projectIndustry !== 'General') ? projectIndustry : detectedIndustry;
      console.log(`🏭 Using industry for bulk-search: ${industryForBulk}`);
      // Prefer project primary category so bulk-search matches project (e.g. SUVs & Crossovers, not default Technology)
      const projectCategory = projectData?.category ?? (projectData?.categories?.find((c: { isPrimary?: boolean }) => c.isPrimary)?.name);
      const categoryForBulk = projectCategory ?? filterData.category ?? 'General';
      // Primary brand = first item; backend /simple-search/bulk-search uses filters.brands[0] as your_brand, brands[1:] as competitor_brands
      const filtersForBulk: any = {
        country: filterData.country,
        brands: filterData.selectedEntities.map(e => e.name),
        competitors: filterData.selectedEntities.slice(1).map(e => e.name),
        category: categoryForBulk,
        industry: industryForBulk,
        language: projectData?.language || 'English',
        project_created_at: filterData.projectCreatedAt || projectData?.created_at
      };
      if (projectData?.categories?.length) {
        filtersForBulk.categories = projectData.categories.map((c: { name: string }) => c.name);
      }
      if ((projectData as any)?.competitors_high?.length) {
        filtersForBulk.competitors_high = (projectData as any).competitors_high.map((c: { name: string }) => c.name);
      }
      if ((projectData as any)?.competitors_low?.length) {
        filtersForBulk.competitors_low = (projectData as any).competitors_low.map((c: { name: string }) => c.name);
      }
      if (projectData?.competitors?.length) {
        filtersForBulk.project_competitors = projectData.competitors
          .map((c: { name?: string }) => (c?.name || '').trim())
          .filter(Boolean);
      }
      
      // Add base_domain to filters if found (for multi-page audit)
      if (baseDomain) {
        filtersForBulk.base_domain = baseDomain;
        filtersForBulk.max_pages = 50; // Default max pages
      }
      
      // Add project description to filters (for backward compatibility)
      if (projectDescription) {
        filtersForBulk.project_description = projectDescription;
      }

      this.lastBulkFiltersSnapshot = { ...filtersForBulk };

      // Overview-prompts-build: Oxylabs uses analysis_categories (cap = MAX_ANALYSIS_CATEGORIES_OXYLABS); phase-2 bulk keeps full list above.
      const overviewCats = this.categoriesForOverviewOxylabs(projectData);
      const filtersOverviewOxylabs =
        overviewCats?.length && filtersForBulk.categories?.length
          ? { ...filtersForBulk, categories: overviewCats }
          : { ...filtersForBulk };
      if ((environment as any).overviewFastMode) {
        (filtersOverviewOxylabs as any).fast_mode = true;
      }

      // True overview-first: run overview-prompts-build + category-analysis in loader, run remaining tabs in phase-2 background.
      sessionStorage.setItem(BULK_OVERVIEW_PROMPTS_ONLY_KEY, '1');
      this.bulkSearchService.startBulkAnalysis(
        this.currentProjectId || undefined,
        this.currentProjectdataId || undefined,
        filtersOverviewOxylabs,
        false,
        ['overview-prompts-build', 'category-analysis']
      ).then(({ taskId }) => {
        console.log(`✅ Bulk search started with task ID: ${taskId} (overview + category analysis + consumer insights)`);
        this.currentTaskIdSubject.next(taskId);
        /** One 0–100% bar: 8 phase-1 + 6 phase-2 “steps” (see UNIFIED_BULK_TOTAL_TABS). */
        this.globalLoaderService.initializeProgress(UNIFIED_BULK_TOTAL_TABS, true);

        // Start progress polling
        this.bulkSearchService.startProgressPolling(taskId, (status: TaskStatus) => {
          const tabLabel = (t: string) => {
            if (t === 'overview-prompts-build') return 'Overview';
            if (t === 'category-analysis') return 'Category Analysis';
            if (t === 'ai-visibility') return 'AI Visibility';
            if (t === 'consumer-insights') return 'Consumer Insights';
            if (t === 'ai-content-index') return 'AI Content Index';
            if (t === 'content-creation') return 'Content Creation';
            if (t === 'performance-tracking') return 'Performance Tracking';
            if (t === 'sentiment-tracking') return 'Sentiment Tracking';
            if (t === 'prompt-creation') return 'Prompt Creation';
            return t || 'Overview';
          };
          let currentTabDisplay = tabLabel(status.current_tab || 'overview');
          if (status.status === 'queued') {
            currentTabDisplay =
              (status.current_step || '').trim() || 'Waiting in queue for a worker…';
          }
          if (status.completed_tab_names?.length && status.status === 'completed') {
            const lastCompleted = status.completed_tab_names[status.completed_tab_names.length - 1];
            currentTabDisplay = `Completed: ${tabLabel(lastCompleted)}`;
          }

          // Backend is source of truth for unified progress now.
          const unifiedPct = Math.min(100, Math.max(0, status.progress_percentage ?? 0));
          const displayCompletedTabs = Math.min(
            UNIFIED_BULK_TOTAL_TABS,
            Math.round((unifiedPct / 100) * UNIFIED_BULK_TOTAL_TABS)
          );

          const eta =
            status.estimated_time_remaining != null && status.estimated_time_remaining > 0
              ? status.estimated_time_remaining
              : null;
          const servedFromCache = this.isCacheStatus(status);

          const backendStage =
            status.overview_stage_step != null && status.overview_stage_total != null
              ? {
                  step: status.overview_stage_step,
                  total: status.overview_stage_total,
                  label: this.sanitizeLoaderLabel(
                    status.overview_stage_label ||
                      status.current_step ||
                      status.current_tab ||
                      'Working...'
                  )
                }
              : status.current_step
                ? { step: 2, total: 3, label: this.sanitizeLoaderLabel(status.current_step) }
                : null;

          this.globalLoaderService.updateProgress(
            unifiedPct,
            displayCompletedTabs,
            status.completed_tab_names || [],
            currentTabDisplay,
            eta,
            UNIFIED_BULK_TOTAL_TABS,
            backendStage,
            null,
            servedFromCache
          );
          
          if (status.status === 'completed') {
            console.log('🎉 Overview bulk tab completed');
            this.globalLoaderService.resetLoader();
            this.bulkSearchService.stopProgressPolling();
            this.currentTaskIdSubject.next(null);
            const pdataId = status.projectdata_id || this.currentProjectdataId || undefined;
            const pid = this.currentProjectId;
            if (pdataId) {
              this.currentProjectdataId = pdataId;
            }
            sessionStorage.removeItem(BULK_OVERVIEW_PROMPTS_ONLY_KEY);
            if (pid && this.lastBulkFiltersSnapshot) {
              void (async () => {
                let phase2Started = false;
                try {
                  const { taskId } = await this.bulkSearchService.startBulkAnalysis(
                    pid,
                    pdataId,
                    this.lastBulkFiltersSnapshot as Record<string, unknown>,
                    false,
                    [...BULK_TABS_AFTER_OVERVIEW]
                  );
                  console.log(`✅ Remaining tabs bulk started: ${taskId}`);
                  sessionStorage.setItem(
                    BULK_REMAINING_TABS_SESSION_KEY,
                    JSON.stringify({
                      taskId,
                      total: UNIFIED_BULK_TOTAL_TABS,
                      projectdataId: pdataId ?? null,
                    })
                  );
                  phase2Started = true;
                } catch (e) {
                  console.error('Remaining tabs bulk failed to start:', e);
                  this.setError('Overview completed, but loading other tabs failed. Use "Reload Other Tabs" in Overview.');
                  // Unlock tab-level APIs so manual reloads (including "Reload Other Tabs") can run.
                  this.isApiCallInProgress = false;
                  this.isBulkSearchWithOverviewInProgress = false;
                  this.setLoading(false);
                }
                if (phase2Started) {
                  const nextUrl = pdataId
                    ? `/common-ai-analysis/generic/${pid}?projectdataId=${pdataId}`
                    : `/common-ai-analysis/generic/${pid}`;
                  window.location.href = nextUrl;
                }
              })();
            } else if (pdataId && pid) {
              window.location.href = `/common-ai-analysis/generic/${pid}?projectdataId=${pdataId}`;
            } else {
              this.handleBulkAnalysisCompleteWithOverview(filterData);
            }
          } else if (status.status === 'failed') {
            console.error('❌ Bulk analysis failed:', status.error_message);
                this.globalLoaderService.resetLoader();
            this.bulkSearchService.stopProgressPolling();
            this.currentTaskIdSubject.next(null);
            this.handleBulkAnalysisError(status.error_message || 'Bulk analysis failed');
          }
        });
      }).catch(error => {
        console.error('❌ Failed to start bulk search:', error);
        this.handleBulkAnalysisError(error);
      });
  }

  /**
   * Resume bulk analysis progress when page is refreshed while a task is already running.
   * Skips calling bulk-search API; initializes global loader and starts progress polling for existing task_id.
   */
  resumeBulkAnalysisProgress(
    taskId: string,
    projectId?: string,
    projectdataId?: string,
    totalTabs: number = 9,
    filterData?: FilterData
  ): void {
    console.log(`🔄 Resuming bulk analysis progress for task: ${taskId} (no new API call)`);
    this.setProjectContext(projectId, projectdataId);
    this.isBulkSearchWithOverviewInProgress = true;
    this.currentTaskIdSubject.next(taskId);
    this.globalLoaderService.initializeProgress(UNIFIED_BULK_TOTAL_TABS, true);
    this.setError(null);

    const onStatus = (status: TaskStatus): void => {
      const apiTotal = status.total_tabs ?? totalTabs;

      let currentTabDisplay = '';
      if (status.current_tab) {
        currentTabDisplay = this.sanitizeLoaderLabel(status.current_tab);
      } else if (status.completed_tab_names?.length) {
        const lastCompleted = status.completed_tab_names[status.completed_tab_names.length - 1];
        currentTabDisplay = `Completed: ${this.sanitizeLoaderLabel(lastCompleted)}`;
      } else {
        currentTabDisplay = `Processing... (${status.completed_tabs ?? 0}/${apiTotal} tabs)`;
      }

      const unifiedPct = Math.min(100, Math.max(0, status.progress_percentage ?? 0));
      const displayCompletedTabs = Math.min(
        UNIFIED_BULK_TOTAL_TABS,
        Math.round((unifiedPct / 100) * UNIFIED_BULK_TOTAL_TABS)
      );
      const servedFromCache = this.isCacheStatus(status);

      this.globalLoaderService.updateProgress(
        unifiedPct,
        displayCompletedTabs,
        status.completed_tab_names ?? [],
        currentTabDisplay,
        status.estimated_time_remaining ?? null,
        UNIFIED_BULK_TOTAL_TABS,
        undefined,
        status.elapsed_time ?? null,
        servedFromCache
      );

      if (apiTotal > 1) {
        const label = this.sanitizeLoaderLabel((status.current_tab || '').replace(/-/g, ' ')) || '';
        this.backgroundTabsProgressSubject.next({
          loaded: displayCompletedTabs,
          total: UNIFIED_BULK_TOTAL_TABS,
          currentTab: label,
          taskId,
        });
      }

      if (status.status === 'completed') {
        console.log('🎉 Bulk analysis completed (resumed session)');
        this.backgroundTabsProgressSubject.next(null);
        this.globalLoaderService.resetLoader();
        this.bulkSearchService.stopProgressPolling();
        this.currentTaskIdSubject.next(null);
        this.isBulkSearchWithOverviewInProgress = false;
        if (status.projectdata_id && this.currentProjectId) {
          this.currentProjectdataId = status.projectdata_id;
          const url = `/common-ai-analysis/generic/${this.currentProjectId}?projectdataId=${status.projectdata_id}`;
          window.location.href = url;
        } else {
          this.handleBulkAnalysisCompleteWithOverview(filterData ?? this.getCurrentFilterData() ?? {} as FilterData);
        }
      } else if (status.status === 'failed' || status.status === 'cancelled') {
        console.error('❌ Bulk analysis failed:', status.error_message);
        this.backgroundTabsProgressSubject.next(null);
        this.globalLoaderService.resetLoader();
        this.bulkSearchService.stopProgressPolling();
        this.currentTaskIdSubject.next(null);
        this.isBulkSearchWithOverviewInProgress = false;
        if (status.status === 'failed') {
          this.handleBulkAnalysisError(status.error_message || 'Bulk analysis failed');
        }
      }
    };

    this.bulkSearchService.startProgressPolling(taskId, onStatus);
  }

  /**
   * Check if project description contains a URL
   */
  private hasUrlInDescription(description: string | null | undefined): boolean {
    if (!description) return false;
    // Pattern 1: Full URLs (http:// or https://)
    const urlPattern = /https?:\/\/[^\s\)\]\>\"\']+/;
    if (urlPattern.test(description)) return true;
    // Pattern 2: Domain patterns (www.example.com or example.com)
    const domainPattern = /(?:www\.)?[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+/;
    return domainPattern.test(description);
  }

  /**
   * Extract base domain from project description
   * Returns base domain URL (e.g., "https://www.mg.co.uk") for multi-page audit
   * Made public so it can be accessed from components
   */
  extractBaseDomain(description: string | null | undefined): string | null {
    if (!description) return null;
    
    try {
      // Pattern 1: Full URLs (http:// or https://)
      const urlPattern = /https?:\/\/[^\s\)\]\>\"\']+/;
      const urlMatches = description.match(urlPattern);
      
      if (urlMatches && urlMatches.length > 0) {
        const url = urlMatches[0].replace(/[.,;:!?)]+$/, ''); // Remove trailing punctuation
        try {
          const urlObj = new URL(url);
          // Return base domain (scheme + netloc)
          return `${urlObj.protocol}//${urlObj.host}`;
        } catch (e) {
          // Invalid URL, try next pattern
        }
      }
      
      // Pattern 2: Domain patterns (www.example.com or example.com)
      const domainPattern = /(?:www\.)?[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+/;
      const domainMatches = description.match(domainPattern);
      
      if (domainMatches && domainMatches.length > 0) {
        const domain = domainMatches[0];
        // Construct URL with https scheme
        return `https://${domain}`;
      }
      
      return null;
    } catch (e) {
      console.warn('Error extracting base domain:', e);
      return null;
    }
  }

  /**
   * Build bulk tab requests excluding overview
   */
  private buildBulkTabRequestsExcludingOverview(filterData: FilterData, forceRefresh: boolean = false): any[] {
    const tabRequests = [];
    const tabsToAnalyze = [
      'ai-citations',
      'category-analysis', 
      'consumer-insights',
      'content-creation',
      'performance-tracking',
      'ai-content-index',
      'sentiment-tracking',
      'prompt-creation',
      'overview'
    ];

    // Audit tabs (eeat-audit, landing-page-audit, ai-audit) are removed from bulk search
    // They can only be run individually via Re-Audit buttons

    for (const tabName of tabsToAnalyze) {
      // Handle EEAT audit as special case (no prompt needed, handled by backend)
      if (tabName === 'eeat-audit') {
        tabRequests.push({
          tab_name: tabName,
          prompt: '', // Not used for EEAT audit
          web_search: false,
          brand_analysis: false,
          your_brand: filterData.selectedEntities[0]?.name || '',
          competitor_brands: filterData.selectedEntities.slice(1).map(entity => entity.name),
          structured_response: true,
          reload: forceRefresh
        });
      } else if (tabName === 'landing-page-audit') {
        // Handle Landing Page audit as special case (no prompt needed, handled by backend)
        // Extract base domain from project description for multi-page audit
        const projectData = this.getCurrentProjectData();
        const projectDescription = projectData?.description || '';
        const baseDomain = this.extractBaseDomain(projectDescription);
        
        const landingPageTabRequest: any = {
          tab_name: tabName,
          prompt: '', // Not used for Landing Page audit
          web_search: false,
          brand_analysis: false,
          your_brand: filterData.selectedEntities[0]?.name || '',
          competitor_brands: filterData.selectedEntities.slice(1).map(entity => entity.name),
          structured_response: true,
          reload: forceRefresh
        };
        
        // Add base_domain if found (enables multi-page audit)
        if (baseDomain) {
          landingPageTabRequest.base_domain = baseDomain;
        }
        
        tabRequests.push(landingPageTabRequest);
      } else if (tabName === 'ai-audit') {
        // Handle AI Audit as special case (no prompt needed, handled by backend)
        tabRequests.push({
          tab_name: tabName,
          prompt: '', // Not used for AI Audit
          web_search: false,
          brand_analysis: false,
          your_brand: filterData.selectedEntities[0]?.name || '',
          competitor_brands: filterData.selectedEntities.slice(1).map(entity => entity.name),
          structured_response: true,
          reload: forceRefresh
        });
      } else if (tabName === 'prompt-creation') {
        // Handle Prompt Creation tab: backend creates 2-3 researches for the project
        tabRequests.push({
          tab_name: tabName,
          prompt: '',
          web_search: false,
          brand_analysis: false,
          your_brand: filterData.selectedEntities[0]?.name || '',
          competitor_brands: filterData.selectedEntities.slice(1).map(entity => entity.name),
          structured_response: false,
          reload: forceRefresh
        });
      } else {
        const prompt = this.buildPromptForTab(tabName, filterData);
        if (prompt) {
          tabRequests.push({
            tab_name: tabName,
            prompt: prompt,
            web_search: true,
            brand_analysis: false,
            your_brand: filterData.selectedEntities[0]?.name || '',
            competitor_brands: filterData.selectedEntities.slice(1).map(entity => entity.name),
            structured_response: true,
            reload: forceRefresh
          });
        }
      }
    }

    return tabRequests;
  }

  /**
   * Build prompt for specific tab
   */
  private buildPromptForTab(tabName: string, filterData: FilterData): string | null {
    // Ensure we have projectCreatedAt - get from filterData, or fallback to projectDataSubject
    const currentProject = this.projectDataSubject.value;
    const projectCreatedAt = filterData.projectCreatedAt || currentProject?.created_at;
    
    // Log for debugging
    if (!projectCreatedAt) {
      console.warn('[buildPromptForTab] No projectCreatedAt found in filterData or projectData');
    } else {
      console.log('[buildPromptForTab] Using projectCreatedAt:', projectCreatedAt);
    }
    
    const params = {
      brands: filterData.selectedEntities.map(entity => entity.name),
      industry: this.getCurrentIndustry(),
      country: filterData.country,
      category: filterData.category,
      projectCreatedAt: projectCreatedAt
    };

    switch (tabName) {
      case 'ai-citations':
        return GenericPromptBuilder.buildAICitationsPrompt(params);
      case 'category-analysis':
        return GenericPromptBuilder.buildCategoryAnalysisPrompt(params);
      case 'consumer-insights':
        return GenericPromptBuilder.buildConsumerInsightsPrompt(params);
      case 'content-creation':
        return GenericPromptBuilder.buildContentCreationPrompt(params);
      case 'performance-tracking':
        return GenericPromptBuilder.buildPerformanceTrackingPrompt(params);
      case 'ai-content-index':
        return GenericPromptBuilder.buildAIContentIndexPrompt(params);
      case 'sentiment-tracking':
        return GenericPromptBuilder.buildSentimentTrackingPrompt(params);
      case 'overview':
        return GenericPromptBuilder.buildGenericOverviewPrompt(params);
      default:
        return null;
    }
  }

  /**
   * Handle bulk analysis completion
   */
  private handleBulkAnalysisCompleteWithOverview(filterData: FilterData): void {
    // Show progress for overview generation (tabs completed from bulk search, overview runs separately)
    // Count completed tabs (including landing page audit if URL detected)
      const projectData = this.getCurrentProjectData();
      const projectDescription = projectData?.description || '';
      const hasLandingPageAudit = this.hasUrlInDescription(projectDescription);
      const completedTabs = hasLandingPageAudit 
        ? ['ai-citations', 'category-analysis', 'consumer-insights', 'content-creation', 'performance-tracking', 'ai-content-index', 'sentiment-tracking', 'prompt-creation', 'eeat-audit', 'ai-audit', 'landing-page-audit']
        : ['ai-citations', 'category-analysis', 'consumer-insights', 'content-creation', 'performance-tracking', 'ai-content-index', 'sentiment-tracking', 'prompt-creation', 'eeat-audit', 'ai-audit'];
      
      this.globalLoaderService.updateProgress(
        95, // Almost complete, just overview left
        completedTabs.length, // Number of tabs completed from bulk search
        completedTabs,
        'Generating overview...',
        null
      );
    
    // Trigger overview analysis (EEAT audit is now handled in bulk-search)
    this.triggerOverviewAnalysisAfterBulk(filterData);
    
    // Start LLM comparison metrics calculation in background (async, non-blocking)
    // This will update the overview tab when ready. Pass project context for DB persistence.
    const projectId = this.currentProjectId || undefined;
    const projectdataId = this.currentProjectdataId || undefined;
    this.startLLMComparisonMetricsCalculation(filterData, projectdataId, projectId);
  }

  /**
   * Trigger overview analysis after bulk completion
   */
  private triggerOverviewAnalysisAfterBulk(filterData: FilterData): void {
    console.log('🎯 Triggering overview analysis after bulk completion');
    
    // Reset API call flag to allow overview analysis
    this.isApiCallInProgress = false;
    
    // Ensure we have filter data with project creation date
    let currentFilterData = this.getCurrentFilterData();
    if (!currentFilterData) {
      currentFilterData = filterData;
    }
    
    // Ensure projectCreatedAt is set - get from project data if not in filterData
    if (!currentFilterData.projectCreatedAt) {
      const currentProject = this.projectDataSubject.value;
      const projectCreatedAt = currentProject?.created_at || filterData.projectCreatedAt;
      
      if (projectCreatedAt) {
        currentFilterData = {
          ...currentFilterData,
          projectCreatedAt: projectCreatedAt
        };
        this.filterDataSubject.next(currentFilterData);
        console.log(`🔧 Overview trigger: Using project creation date: ${projectCreatedAt}`);
      } else {
        // Fallback to current date only if no project data available
        currentFilterData = {
          ...currentFilterData,
          projectCreatedAt: new Date().toISOString()
        };
        this.filterDataSubject.next(currentFilterData);
        console.warn(`🔧 Overview trigger: No project data found, using current date for cache key`);
      }
    }
    
    console.log('📊 Triggering overview with filter data:', currentFilterData);
    
    // Respect hard reload flag; default to cached (no force) unless hardReload is set
    const hardReload = (currentFilterData as any)?.hardReload === true;
    
    // Force fresh API call for overview after bulk search completion (bypass cache check)
    // but respect the hardReload flag for the actual reload parameter
    this.triggerOverviewAnalysisDirectly(currentFilterData, hardReload);
  }

  /**
   * Trigger overview analysis directly (bypass cache check) after bulk completion
   */
  private triggerOverviewAnalysisDirectly(filterData: FilterData, hardReload: boolean): void {
    console.log(`🚀 Triggering overview analysis directly, hardReload: ${hardReload}`);
    
    // Reset API call flag to allow overview analysis
    this.isApiCallInProgress = false;
    
    if (filterData && filterData.selectedEntities.length === 0) {
      this.setError('Please select at least one entity to analyze');
      return;
    }

    this.setLoading(true);
    this.setError(null);
    this.isApiCallInProgress = true;

    // After bulk-search, data is already fresh, so use reload: false
    // This prevents unnecessary cache bypass when data is already up-to-date
    this.callGenericTabAPIWithReload(filterData, 'overview', false);
  }

  private buildFiltersForRemainingTabsBulk(): Record<string, unknown> {
    const filterData = this.getCurrentFilterData();
    const projectData = this.getCurrentProjectData();
    const selectedEntities = filterData?.selectedEntities || [];
    const brands = selectedEntities.map((e) => e?.name).filter(Boolean);
    const competitors = brands.slice(1);
    const projectDescription = projectData?.description || '';
    const baseDomain = this.extractBaseDomain(projectDescription);
    const projectCategory = projectData?.category ?? (projectData?.categories?.find((c: { isPrimary?: boolean }) => c.isPrimary)?.name);
    const categoryForBulk = projectCategory ?? filterData?.category ?? 'General';
    const projectIndustry = projectData?.industry ?? null;
    const detectedIndustry = this.getCurrentIndustry();
    const industryForBulk = (projectIndustry && projectIndustry !== 'General') ? projectIndustry : detectedIndustry;

    const filtersForBulk: any = {
      country: filterData?.country || projectData?.primary_country || 'Global',
      brands,
      competitors,
      category: categoryForBulk,
      industry: industryForBulk,
      language: projectData?.language || 'English',
      project_created_at: filterData?.projectCreatedAt || projectData?.created_at
    };

    if (projectData?.categories?.length) {
      filtersForBulk.categories = projectData.categories.map((c: { name: string }) => c.name);
    }
    if (projectData?.competitors?.length) {
      filtersForBulk.project_competitors = projectData.competitors
        .map((c: { name?: string }) => (c?.name || '').trim())
        .filter(Boolean);
    }
    if (baseDomain) {
      filtersForBulk.base_domain = baseDomain;
      filtersForBulk.max_pages = 50;
    }
    if (projectDescription) {
      filtersForBulk.project_description = projectDescription;
    }
    return filtersForBulk;
  }

  async reloadOtherTabsBulk(): Promise<{ started: boolean; taskId?: string; message?: string }> {
    const context = this.getProjectContextFromRouter();
    const pid = this.currentProjectId || context?.projectId || undefined;
    const pdataId = this.currentProjectdataId || context?.projectdataId || undefined;
    if (!pid || !pdataId) {
      return { started: false, message: 'Project context is missing for other-tabs reload.' };
    }

    const filters =
      (this.lastBulkFiltersSnapshot as Record<string, unknown> | null) ||
      this.buildFiltersForRemainingTabsBulk();

    try {
      const { taskId } = await this.bulkSearchService.startBulkAnalysis(
        pid,
        pdataId,
        filters,
        false,
        [...BULK_TABS_AFTER_OVERVIEW]
      );
      sessionStorage.setItem(
        BULK_REMAINING_TABS_SESSION_KEY,
        JSON.stringify({
          taskId,
          total: UNIFIED_BULK_TOTAL_TABS,
          projectdataId: pdataId,
        })
      );
      this.startSilentRemainingTabsPolling(taskId);
      return { started: true, taskId };
    } catch (e: any) {
      const msg = e?.error?.detail || e?.message || 'Failed to start other-tabs bulk.';
      this.setError(msg);
      return { started: false, message: msg };
    }
  }

  /**
   * Handle bulk analysis error
   */
  private handleBulkAnalysisError(error: any): void {
    console.error('❌ Bulk analysis error:', error);
    const status = error?.status ?? error?.error?.status;
    const detail =
      typeof error?.error?.detail === 'string'
        ? error.error.detail
        : error?.message || '';
    if (status === 409) {
      this.setError(
        'Another analysis is still marked active, or a previous cancel did not finish. Please try again in a few seconds. If this persists, refresh the page.'
      );
    } else {
      this.setError(detail ? `Bulk analysis failed: ${detail}` : 'Bulk analysis failed. Please try again.');
    }
    this.setLoading(false);
    this.isApiCallInProgress = false;
    this.isBulkSearchWithOverviewInProgress = false;
    sessionStorage.removeItem(BULK_OVERVIEW_PROMPTS_ONLY_KEY);
    this.bulkSearchService.stopProgressPolling();
    this.currentTaskIdSubject.next(null);
    this.globalLoaderService.resetLoader();
  }

  /**
   * Finalize bulk search with overview
   */
  private finalizeBulkSearchWithOverview(): void {
    console.log('✅ Finalizing bulk search with overview');
    
    // Reset bulk search flag
    this.isBulkSearchWithOverviewInProgress = false;
    
    // Reset global loader and service state
    this.globalLoaderService.resetLoader();
    this.setLoading(false);
    this.isApiCallInProgress = false;
    
    // Reset bulk search service state
    this.bulkSearchService.stopProgressPolling();
    
    // Notify that analysis is completed
    this.analysisCompletedSubject.next(true);
    
    // Reset the flag after a short delay
    setTimeout(() => {
      this.analysisCompletedSubject.next(false);
    }, 100);
  }

  /**
   * Start LLM comparison metrics calculation in background (task-based)
   * This runs asynchronously after bulk search completes
   * Updates overview tab when ready
   * Made public so it can be called from components when loading from history
   * @param filterData - Filter data (brands, category, region, etc.)
   * @param projectdataId - Optional projectdata (version) ID for persisting result
   * @param projectId - Optional project ID for persisting result (backend can resolve projectdata_id from this)
   */
  startLLMComparisonMetricsCalculation(filterData: FilterData, projectdataId?: string, projectId?: string): void {
    console.log('🔄 Starting LLM comparison metrics calculation in background (task-based)...');
    
    const projectData = this.getCurrentProjectData();
    const brands = filterData.selectedEntities?.map(e => e.name) || [];
    const primaryBrand = brands[0];
    const baseDomain = filterData.base_domain || this.extractBaseDomain(projectData?.description) || '';
    const baseDomainLower = (baseDomain || '').toLowerCase();
    const brandLower = (primaryBrand || '').toLowerCase();
    // Use project/history category and categories when available (from GET /generic/history filters)
    const projectCategory = filterData.category;
    const projectCategories = filterData.categories && filterData.categories.length > 0 ? filterData.categories : undefined;
    // MG: primary industry Automotive, primary category Passenger Vehicles (Cars)
    const isMG = brandLower.includes('mg') && (brandLower.includes('mg uk') || brandLower === 'mg' || brandLower.startsWith('mg ')) || baseDomainLower.includes('mg.co.uk');
    let effectiveIndustry: string;
    let category: string;
    if (isMG) {
      effectiveIndustry = 'Automotive';
      category = 'Passenger Vehicles';
    } else {
      const industryFromFilter = filterData.industry && filterData.industry !== 'General' ? filterData.industry : null;
      const industryFromCache = primaryBrand ? this.industryCacheSubject.value.get(primaryBrand) : null;
      effectiveIndustry = industryFromFilter || industryFromCache || 'General';
      // Prefer project category (from history), then industry, then fallback
      category = (projectCategory && projectCategory !== 'General' ? projectCategory : (effectiveIndustry && effectiveIndustry !== 'General' ? effectiveIndustry : filterData.category)) || 'General';
    }
    const country = filterData.country || 'Global';
    
    if (brands.length === 0) {
      console.warn('⚠️ No brands provided for LLM comparison metrics');
      return;
    }
    
    // Project language for prompts and AI responses (from project or default English)
    const language = projectData?.language || (filterData as any)?.language || 'English';
    
    // Prepare request payload: category/categories from project (history), industry for industry average and prompts
    const payload: any = {
      brands: brands,
      category: category,
      industry: effectiveIndustry !== 'General' ? effectiveIndustry : undefined,
      region: country,
      language: language
    };
    const oxylabsCats = projectData?.analysis_categories;
    if (Array.isArray(oxylabsCats) && oxylabsCats.length > 0) {
      payload.categories = oxylabsCats.map((s) => String(s).trim()).filter(Boolean);
    } else if (projectCategories?.length) {
      payload.categories = projectCategories;
    }
    if (baseDomain) {
      payload.base_domain = baseDomain;
    }
    
    // Use explicitly passed projectId/projectdataId, or fall back to project context (set via setProjectContext/loadFunction)
    const projectIdToUse = projectId ?? this.currentProjectId;
    const projectdataIdToUse = projectdataId ?? this.currentProjectdataId;
    
    if (projectIdToUse) {
      payload.project_id = projectIdToUse;
    }
    if (projectdataIdToUse) {
      payload.projectdata_id = projectdataIdToUse;
      console.log(`📦 Using project_id: ${projectIdToUse || 'none'}, projectdata_id: ${projectdataIdToUse}`);
    } else {
      console.warn('⚠️ No projectdata_id available - data may not be saved to projectdata');
    }
    
    // Call the LLM comparison metrics endpoint (returns task_id or existing comparison_data)
    this.genericService.calculateLLMComparisonMetrics(payload).subscribe({
      next: (response) => {
        const taskId = response.task_id ?? response.metadata?.task_id;
        const existingData = response.metadata?.from_history === true && response.comparison_data?.length > 0;

        if (existingData && response.comparison_data?.length) {
          console.log('✅ Using existing LLM comparison data from history (no task started)');
          this.updateOverviewWithLLMComparisonMetrics(response.comparison_data);
          return;
        }

        if (!taskId) {
          console.error('❌ No task_id returned from LLM comparison metrics endpoint');
          return;
        }

        console.log(`✅ LLM comparison metrics task started: ${taskId}`);
        console.log(`   Estimated time: ${response.metadata?.estimated_time || '3-5 minutes'}`);

        this.llmComparisonMetricsTaskIdSubject.next(taskId);

        // Poll GET history (version_id) only – read status/progress from overview.llm_comparison (do not call status API)
        if (projectdataIdToUse) {
          this.startLLMComparisonMetricsPolling(taskId, projectdataIdToUse);
        } else {
          this.startLLMComparisonMetricsPolling(taskId, undefined);
        }
      },
      error: (error) => {
        console.error('❌ Failed to start LLM comparison metrics calculation:', error);
        
        // If error is 409 (conflict - task already exists), extract task_id and resume polling
        if (error.status === 409 && error.error?.detail) {
          const detail = error.error.detail;
          const taskIdMatch = detail.match(/Task ID: ([a-f0-9-]+)/i);
          if (taskIdMatch && taskIdMatch[1]) {
            const existingTaskId = taskIdMatch[1];
            console.log(`🔄 Found existing LLM comparison metrics task: ${existingTaskId}, resuming...`);
            // Store task ID and start polling
            this.llmComparisonMetricsTaskIdSubject.next(existingTaskId);
            this.startLLMComparisonMetricsPolling(existingTaskId);
            return;
          }
        }
        
        // Don't show error to user - this is background calculation
        console.warn('⚠️ Overview tab will display without LLM comparison metrics');
      }
    });
  }

  /**
   * Check for active LLM comparison metrics task and resume if found
   * Called when component initializes to detect active tasks
   */
  checkAndResumeActiveLLMComparisonMetricsTask(): void {
    // Check analysis status to see if there's an active task
    this.analysisStatusService.checkActiveAnalysis().subscribe({
      next: (status) => {
        const looksLikeLlmTask = `${(status as any)?.current_tab || ''}`.toLowerCase().includes('llm_visibility');
        if (status.has_active_analysis && status.task_id && looksLikeLlmTask) {
          const taskId = status.task_id;
          console.log(`🔍 Found active analysis task: ${taskId}, status: ${status.status}`);
          console.log(`✅ Resuming active LLM comparison metrics task over WebSocket: ${taskId}`);
          this.llmComparisonMetricsTaskIdSubject.next(taskId);
          this.startLLMComparisonMetricsPolling(taskId, this.currentProjectdataId ?? undefined);
        }
      },
      error: (error) => {
        console.error('Error checking active analysis:', error);
      }
    });
  }

  /**
   * Start polling for LLM comparison metrics (AI visibility) progress, or subscribe to WebSocket when useWebSocket.
   * Polling code kept for rollback – do not remove.
   */
  startLLMComparisonMetricsPolling(taskId: string, _projectdataId?: string): void {
    if (this.taskProgressWs.useWebSocket) {
      this.llmComparisonMetricsWsSubscription?.unsubscribe();
      this.taskProgressWs.connect();
      this.llmComparisonMetricsWsSubscription = this.taskProgressWs.messages$
        .pipe(filter((msg) => msg.path === 'llm_visibility' && (msg.data as any)?.['task_id'] === taskId))
        .subscribe((msg) => {
          const data = msg.data as unknown as TaskStatus;
          if (!data) return;
          this.llmComparisonMetricsProgressSubject.next(data);
          if (data.status === 'completed') {
            this.stopLLMComparisonMetricsPolling();
            this.llmComparisonMetricsTaskIdSubject.next(null);
            const projectdataId = (data as any).projectdata_id || this.currentProjectdataId;
            if (projectdataId) {
              this.projectHistoryService.getVersionData(projectdataId).subscribe({
                next: (versionData) => {
                  this.llmComparisonCompleteWithDataSubject.next(versionData);
                  this.refreshOverviewAfterLLMComparisonMetrics();
                },
                error: () => this.refreshOverviewAfterLLMComparisonMetrics(),
              });
            } else {
              this.refreshOverviewAfterLLMComparisonMetrics();
            }
          } else if (data.status === 'failed') {
            this.stopLLMComparisonMetricsPolling();
            this.llmComparisonMetricsTaskIdSubject.next(null);
          }
        });
      console.log('📊 Starting LLM comparison metrics (AI visibility) via WebSocket for task:', taskId);
      return;
    }
    console.warn(`WebSocket disabled; skipping status polling fallback for task ${taskId}.`);
  }

  /**
   * Poll GET /generic/history/{version_id}; read overview.llm_comparison.status and .progress (single source of truth).
   */
  private pollLLMComparisonMetricsViaHistory(projectdataId: string): void {
    let pollCount = 0;
    const initialInterval = 2000;
    const regularInterval = 5000;

    const pollTask = () => {
      this.projectHistoryService.getVersionData(projectdataId).subscribe({
        next: (versionData) => {
          const data = versionData?.data || {};
          const llm = data?.overview?.llm_comparison || {};
          const status = llm.status;
          const progress = llm.progress || {};

          const progressStatus: TaskStatus = {
            task_id: llm.task_id || '',
            status: status || 'pending',
            total_tabs: 7,
            completed_tabs: Math.round((progress.percentage || 0) / 100 * 7),
            progress_percentage: progress.percentage ?? 0,
            completed_tab_names: [],
            current_tab: progress.current_step,
            current_step: progress.current_step,
            status_messages: progress.status_messages ?? [],
            elapsed_time: progress.elapsed_time,
            updated_at: progress.updated_at,
            error_message: llm.error_message,
            projectdata_id: projectdataId,
          };
          this.llmComparisonMetricsProgressSubject.next(progressStatus);

          if (status === 'completed') {
            console.log('✅ LLM comparison metrics completed (from history)');
            this.stopLLMComparisonMetricsPolling();
            this.llmComparisonMetricsTaskIdSubject.next(null);
            this.analysisResponseSubject.next(data);
            this.llmComparisonMetricsProgressSubject.next(null);
          } else if (status === 'failed') {
            console.error('❌ LLM comparison metrics failed:', llm.error_message);
            this.stopLLMComparisonMetricsPolling();
            this.llmComparisonMetricsTaskIdSubject.next(null);
            this.llmComparisonMetricsProgressSubject.next(progressStatus);
          }

          pollCount++;
          if (pollCount === 3) {
            clearInterval(this.llmComparisonMetricsPollingInterval!);
            this.llmComparisonMetricsPollingInterval = setInterval(pollTask, regularInterval);
          }
        },
        error: (err) => {
          console.error('Error polling history for LLM comparison:', err);
        },
      });
    };

    this.llmComparisonMetricsPollingInterval = setInterval(pollTask, initialInterval);
  }

  /**
   * Fallback: poll status API when projectdataId is not available (e.g. resume by task_id only).
   */
  private pollLLMComparisonMetricsViaStatusApi(taskId: string): void {
    let pollCount = 0;
    const initialInterval = 2000;
    const regularInterval = 5000;

    const pollTask = async () => {
      try {
        const status = await this.genericService.getLLMComparisonMetricsStatus(taskId).toPromise();
        if (status) {
          this.llmComparisonMetricsProgressSubject.next(status);
          if (status.status === 'completed') {
            this.stopLLMComparisonMetricsPolling();
            this.llmComparisonMetricsTaskIdSubject.next(null);
            const projectdataId = status.projectdata_id || this.currentProjectdataId;
            if (projectdataId) {
              // Fetch project history (data already saved by backend task) and emit for page to distribute to all tabs
              this.projectHistoryService.getVersionData(projectdataId).subscribe({
                next: (versionData) => {
                  this.llmComparisonCompleteWithDataSubject.next(versionData);
                  this.refreshOverviewAfterLLMComparisonMetrics();
                },
                error: () => this.refreshOverviewAfterLLMComparisonMetrics(),
              });
            } else {
              this.refreshOverviewAfterLLMComparisonMetrics();
            }
          } else if (status.status === 'failed') {
            this.stopLLMComparisonMetricsPolling();
            this.llmComparisonMetricsTaskIdSubject.next(null);
          }
          pollCount++;
          if (pollCount === 3) {
            clearInterval(this.llmComparisonMetricsPollingInterval!);
            this.llmComparisonMetricsPollingInterval = setInterval(pollTask, regularInterval);
          }
        }
      } catch (err: any) {
        if (err?.status === 404) {
          this.stopLLMComparisonMetricsPolling();
        }
      }
    };

    this.llmComparisonMetricsPollingInterval = setInterval(pollTask, initialInterval);
  }

  /**
   * Stop polling for LLM comparison metrics (or WebSocket subscription).
   */
  private stopLLMComparisonMetricsPolling(): void {
    if (this.llmComparisonMetricsWsSubscription) {
      this.llmComparisonMetricsWsSubscription.unsubscribe();
      this.llmComparisonMetricsWsSubscription = undefined;
      console.log('⏹️ Stopped LLM comparison metrics WebSocket subscription');
    }
    if (this.llmComparisonMetricsPollingInterval) {
      clearInterval(this.llmComparisonMetricsPollingInterval);
      this.llmComparisonMetricsPollingInterval = undefined;
      console.log('⏹️ Stopped LLM comparison metrics polling');
    }
  }

  /**
   * Cancel LLM comparison metrics task
   */
  cancelLLMComparisonMetricsTask(taskId: string): void {
    console.log(`🛑 Cancelling LLM comparison metrics task: ${taskId}`);
    
    this.genericService.cancelLLMComparisonMetricsTask(taskId).subscribe({
      next: (response) => {
        console.log('✅ LLM comparison metrics task cancelled:', response);
        // Stop polling
        this.stopLLMComparisonMetricsPolling();
        // Clear task ID and progress
        this.llmComparisonMetricsTaskIdSubject.next(null);
        this.llmComparisonMetricsProgressSubject.next(null);
      },
      error: (error) => {
        console.error('❌ Failed to cancel LLM comparison metrics task:', error);
        // Still stop polling and clear state
        this.stopLLMComparisonMetricsPolling();
        this.llmComparisonMetricsTaskIdSubject.next(null);
        this.llmComparisonMetricsProgressSubject.next(null);
      }
    });
  }

  /**
   * Refresh overview tab after LLM comparison metrics complete
   * DO NOT reload the page - data is already updated via updateOverviewWithLLMComparisonMetrics
   */
  private refreshOverviewAfterLLMComparisonMetrics(): void {
    console.log('🔄 LLM comparison metrics completed - overview data already updated');
    // Data is already updated via updateOverviewWithLLMComparisonMetrics()
    // Backend has already saved to projectdata, so no need to reload
    // Just ensure loading state is cleared
    this.llmComparisonMetricsProgressSubject.next(null);
  }

  /**
   * Update overview tab with LLM comparison metrics
   * When overview lacks composite_score/performance_table, synthesize from comparisonData so Business Overview and chart render
   */
  private updateOverviewWithLLMComparisonMetrics(comparisonData: any[]): void {
    let currentOverview = this.analysisResponseSubject.value || {};
    const filters = this.getCurrentFilterData();
    const brands = filters?.selectedEntities?.map((e: any) => e.name) || [];
    const primaryBrand = brands[0] || 'Your Brand';

    // Synthesize composite_score and performance_table if missing (prevents empty overview after AI visibility completes)
    if (!currentOverview.composite_score || !currentOverview.performance_table) {
      let compositeValue = 0;
      const performanceRows: any[] = [];
      if (Array.isArray(comparisonData) && comparisonData.length > 0) {
        comparisonData.forEach((item: any, idx: number) => {
          const brand = item.brand || brands[idx] || `Brand ${idx + 1}`;
          const metrics = item.ai_visibility_metrics || item.llm_metrics || {};
          const vals = [
            metrics.ai_visibility, metrics.visibility_score,
            metrics.chatgpt?.ai_visibility, metrics.gemini?.ai_visibility,
            metrics.ai_overview?.ai_visibility, metrics.ai_mode?.ai_visibility
          ].filter((v: any): v is number => typeof v === 'number');
          const score = vals.length > 0 ? Math.round(vals.reduce((a: number, b: number) => a + b, 0) / vals.length) : 0;
          if (idx === 0) compositeValue = score;
          performanceRows.push({
            brand, is_your_brand: idx === 0,
            brand_reputation: score, market_presence: score, customer_satisfaction: score,
            innovation_score: score, financial_performance: score, industry_influence: score,
            market_share: 'N/A', ranking: 'N/A'
          });
        });
      }
      currentOverview = {
        ...currentOverview,
        composite_score: currentOverview.composite_score || {
          value: compositeValue,
          label: compositeValue >= 80 ? 'Very Good' : compositeValue >= 60 ? 'Good' : compositeValue >= 40 ? 'Average' : 'Needs Improvement',
          color: compositeValue >= 80 ? '#4caf50' : compositeValue >= 60 ? '#ff9800' : compositeValue >= 40 ? '#2196f3' : '#f44336',
          brand: primaryBrand
        },
        performance_table: currentOverview.performance_table || (performanceRows.length > 0 ? performanceRows : [{
          brand: primaryBrand, is_your_brand: true,
          brand_reputation: compositeValue, market_presence: compositeValue, customer_satisfaction: compositeValue,
          innovation_score: compositeValue, financial_performance: compositeValue, industry_influence: compositeValue,
          market_share: 'N/A', ranking: 'N/A'
        }]),
        metadata: {
          ...(currentOverview.metadata || {}),
          industry: currentOverview.metadata?.industry || filters?.industry || 'N/A',
          your_brand: currentOverview.metadata?.your_brand || primaryBrand,
          competitors: currentOverview.metadata?.competitors ?? brands.slice(1) ?? [],
          brands_analyzed: currentOverview.metadata?.brands_analyzed ?? (performanceRows.length || 1),
          data_source: currentOverview.metadata?.data_source || 'AI Visibility'
        }
      };
    }

    const updatedOverview = {
      ...currentOverview,
      llm_comparison: {
        comparison_data: comparisonData,
        metadata: {
          data_source: 'real-time',
          timestamp: new Date().toISOString(),
          models_available: ['chatgpt', 'gemini']
        }
      }
    };

    this.analysisResponseSubject.next(updatedOverview);
    console.log('✅ Updated overview tab with LLM comparison metrics');
  }

  /**
   * Update projectdata with LLM comparison metrics (backend should already do this, but ensure it's done)
   */
  private updateProjectdataWithLLMComparisonMetrics(comparisonData: any[]): void {
    // Backend already updates projectdata when task completes
    // This is just a safety check - backend handles it in _process_llm_comparison_metrics_task
    console.log('✅ Projectdata will be updated by backend automatically');
  }

  /**
   * Call generic overview API (fallback for non-bulk searches)
   */
  private callGenericOverviewAPI(filterData: FilterData, forceRefresh: boolean = false): void {
    console.log('📊 Calling generic overview API');
    
    // Trigger overview tab analysis
    this.triggerTabAnalysis('overview', false, forceRefresh);
  }

  /**
   * Get current industry from cache or default
   */
  private getCurrentIndustry(): string {
    const currentFilterData = this.getCurrentFilterData();
    if (currentFilterData?.selectedEntities?.[0]?.name) {
      const brandName = currentFilterData.selectedEntities[0].name;
      const cachedIndustry = this.getCachedIndustry(brandName);
      if (cachedIndustry) {
        console.log(`📊 Retrieved industry from cache for ${brandName}: ${cachedIndustry}`);
        return cachedIndustry;
      } else {
        console.warn(`⚠️ No cached industry found for ${brandName}, using 'General'`);
        return 'General';
      }
    }
    console.warn(`⚠️ No brand found in filter data, using 'General'`);
    return 'General';
  }

  /**
   * Initialize global loader for bulk analysis (called early in Re-Analysis flow)
   * This ensures the loader shows immediately, even before project data is loaded
   */
  initializeGlobalLoaderForBulkAnalysis(): void {
    console.log('🚀 Initializing global loader early for bulk analysis');
    this.globalLoaderService.initializeProgress();
    this.globalLoaderService.updateProgressPercentage(0);
  }
}
